import type { PortalApi, StudioApi } from './client';
import { everyLabel } from '@/lib/rhythm';
import { invoiceDate, periodLabel } from '@/lib/billing';
import type {
  CalendarLink,
  IntegrationsHealth,
  LegalSchema,
  UndeliveredNotification,
  LegalValues,
  Act,
  ActInput,
  ActSourceLine,
  InvoiceInput,
  InvoiceLine,
  AuditAction,
  AuditFilter,
  AuditEvent,
  PersonAccess,
  StudioRole,
  TeamMember,
  ActionItem,
  ChangeDetail,
  ChangeEvent,
  ChangeRequest,
  InitiativeKind,
  NewInitiative,
  ClientContact,
  ColleaguePatch,
  PersonPatch,
  Feedback,
  Meeting,
  MeetingInput,
  MeetingPatch,
  MeetingScope,
  Notification,
  NotificationPrefs,
  OnboardingItem,
  PayerDetails,
  PayerSummary,
  InvoicePayer,
  Proposal,
  Report,
  SignRequest,
  CheckoutProblem,
  StatementLine,
  MarkPaidInput,
  StatementPick,
  CatalogItem,
  Client,
  ClientSummary,
  StudioAttention,
  StudioThread,
  StudioThreadFilter,
  Activity,
  CheckoutSession,
  DocumentRef,
  Invoice,
  Message,
  Money,
  PaymentMethod,
  Person,
  Project,
  SearchHit,
  Secret,
  Subscription,
  Task,
  TaskStatus,
  Thread,
  NewDiscussion,
  Workspace,
  MyProfile,
  MySession,
  PersonCard,
  ProfilePatch,
  ApiCall,
  ApiKey,
  ApiPreset,
  ApiScope,
  ClientIntegrations,
  ClientWebhooks,
  IssuedApiKey,
  NewApiKey,
  NewWebhookEndpoint,
  WebhookDeliveriesPage,
  WebhookDelivery,
  WebhookDeliveryFilter,
  WebhookEndpoint,
  WebhookPatch,
} from './types';
import type { ProfileApi } from './profiles';
import { ApiError } from './http';
import { mockOpenapi } from './mockOpenapi';
import { isUrgent, sortAttention } from '@/lib/attention';
import { isCanceled, meetingEnd } from '@/lib/meetings';
import { decryptSecret, encryptSecret, type SealedSecret } from '@/lib/crypto';

// Demo data for local development. People are invented; the client and the
// work are shaped after the studio's first real engagement. Volumes are large
// on purpose (dozens of tasks, hundreds of messages) so the UI is judged at scale.

const rub = (rubles: number): Money => ({ amountMinor: rubles * 100, currency: 'RUB' });
const rubMinor = (amountMinor: number): Money => ({ amountMinor, currency: 'RUB' });

const people = {
  me: { id: 'u-client', name: 'Алексей Громов', role: 'Владелец, Карандаш', side: 'client', initials: 'АГ' },
  accountant: { id: 'u-acc', name: 'Ирина Белова', role: 'Бухгалтерия, Карандаш', side: 'client', initials: 'ИБ' },
  manager: { id: 'u-pm', name: 'Родион', role: 'Руководитель проектов', side: 'studio', initials: 'Р' },
  designer: { id: 'u-des', name: 'Мария Орлова', role: 'Дизайнер', side: 'studio', initials: 'МО' },
  dev: { id: 'u-dev', name: 'Олег Шмидт', role: 'Разработчик', side: 'studio', initials: 'ОШ' },
  ads: { id: 'u-ads', name: 'Павел Ким', role: 'Реклама и аналитика', side: 'studio', initials: 'ПК' },
} satisfies Record<string, Person>;

// The client whose portal this demo renders; the studio side sees every client.
const CLIENT_ID = 'karandash';

/** The demo is the RU contour: the same schema portal-bff serves from config/ru.yaml. */
export const RU_LEGAL: LegalSchema = {
  country: 'RU',
  fields: [
    { key: 'legalName', label: 'Название организации или ИП', required: true, kind: 'text', wide: true, numeric: false, hint: '', column: 'legal_name' },
    { key: 'taxId', label: 'ИНН', required: true, kind: 'text', wide: false, numeric: true, hint: '', column: 'tax_id' },
    { key: 'kpp', label: 'КПП', required: false, kind: 'text', wide: false, numeric: true, hint: '', column: null },
    { key: 'ogrn', label: 'ОГРН / ОГРНИП', required: false, kind: 'text', wide: false, numeric: true, hint: '', column: null },
    { key: 'address', label: 'Юридический адрес', required: false, kind: 'text', wide: true, numeric: false, hint: '', column: null },
  ],
  acts: true,
  totalLabel: 'Итого, без НДС',
  taxNote: 'Без НДС (исполнитель применяет УСН)',
  invoiceNote: '',
  payerTitle: 'Реквизиты плательщика',
  transferQr: 'ST00012',
  basis: 'Счёт-оферта',
  paymentPurpose: 'Оплата по счёту № {number} от {date}. Без НДС',
};

/** Same checks as the BFF for the RU schema, so the demo answers like the live portal. */
function checkRuLegal(v: LegalValues, partial = false) {
  const errors: string[] = [];
  if (!partial && !v.legalName?.trim()) errors.push('заполните поле «Название организации или ИП»');
  if ((v.taxId || !partial) && !/^\d{10}(\d{2})?$/.test(v.taxId ?? '')) errors.push('ИНН — 10 цифр для организации или 12 для ИП');
  if (v.kpp && !/^\d{9}$/.test(v.kpp)) errors.push('КПП — 9 цифр; у ИП его нет, оставьте пустым');
  if (v.ogrn && !/^(\d{13}|\d{15})$/.test(v.ogrn)) errors.push('ОГРН — 13 цифр, ОГРНИП — 15');
  if (errors.length) throw new Error(errors.join('; ').replace(/^./, (c) => c.toUpperCase()));
}

const workspace: Workspace = {
  id: 'ws-karandash',
  clientName: 'Карандаш',
  me: { ...people.me, clientRole: 'owner' },
  people: Object.values(people),
  studio: { name: 'Mark-42', manager: people.manager },
  contour: {
    code: 'ru',
    currency: 'RUB',
    channels: ['email'],
    statement: true,
    legal: RU_LEGAL,
    docs: { offer: 'https://mark-42.ru/offer/', privacy: 'https://mark-42.ru/privacy/' },
    payments: {
      provider: 'tochka',
      online: ['sbp', 'card'],
      transfer: { payee: 'ИП, реквизиты контура (демо)', taxId: '000000000000', bank: 'АО «ТОЧКА»', bic: '044525104', correspondentAccount: '30101810745374525104', account: '40802810000000000000' },
    },
  },
};

// Deterministic pseudo-random so the demo looks the same on every reload.
let seed = 42;
const rnd = () => {
  seed = (seed * 1664525 + 1013904223) % 4294967296;
  return seed / 4294967296;
};
const pick = <T,>(xs: readonly T[]) => xs[Math.floor(rnd() * xs.length)];

// Anchor the story to the current day so relative dates read naturally.
const NOW = Date.now();
const hoursAgo = (h: number) => new Date(NOW - h * 3600_000).toISOString();
const day = (offset: number) => {
  const d = new Date(NOW + offset * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Projects

const projects: Project[] = [
  {
    kind: 'fixed',
    id: 'site',
    clientId: 'karandash',
    linear: { name: 'Карандаш — новый сайт', url: 'https://linear.app/rodion-ch/project/site' },
    key: 'SITE',
    title: 'Новый сайт karandashnw.ru',
    summary: 'Сайт, который продаёт ремонт под ключ: квиз на первом экране, реальные объекты, отзывы.',
    health: 'waiting_client',
    now: 'Прототипы почти готовы. Дизайн стартует, как только вы утвердите главную.',
    owner: people.manager,
    team: [people.manager, people.designer, people.dev],
    updatedAt: hoursAgo(2.5),
    finishBy: day(47),
    milestones: [
      { id: 'brief', title: 'Бриф', state: 'done', due: day(-17) },
      { id: 'proto', title: 'Прототипы', state: 'current', due: day(4) },
      { id: 'design', title: 'Дизайн', state: 'next', due: day(18) },
      { id: 'build', title: 'Вёрстка и CMS', state: 'next', due: day(39) },
      { id: 'launch', title: 'Запуск', state: 'next', due: day(47) },
    ],
    terms: { price: rub(180000), contract: 'Договор № 12-2026', since: day(-28) },
  },
  {
    kind: 'retainer',
    id: 'ads',
    clientId: 'karandash',
    linear: { name: 'Клиент Карандаш и New Tone — сайты, лендинг, реклама', url: 'https://linear.app/rodion-ch/project/ads' },
    key: 'ADS',
    title: 'Реклама и аналитика',
    summary: 'Директ, Яндекс Бизнес и сквозная аналитика до заявки в amoCRM.',
    health: 'on_track',
    now: 'Заявок стало больше после чистки запросов. На этой неделе запускаем кампанию на загородные дома.',
    owner: people.ads,
    team: [people.ads, people.manager],
    updatedAt: hoursAgo(9),
    period: 'эта неделя',
    rhythm: [
      { id: 'r1', title: 'Отчёт по рекламе', cadence: 'Каждую неделю', every: 'week', source: 'report', next: day(6), lastDone: day(-1) },
      { id: 'r2', title: 'Чистка запросов и ставок', cadence: 'Каждую неделю', every: 'week', source: 'linear', label: 'rhythm:direct', taskKeys: ['ADS-14'], next: day(1), lastDone: day(-2) },
      { id: 'r3', title: 'Созвон по результатам', cadence: 'Раз в две недели', every: '2weeks', source: 'meeting', next: day(3), lastDone: day(-11) },
      { id: 'r4', title: 'Сверка заявок с amoCRM', cadence: 'Каждую неделю', every: 'week', source: 'manual', next: day(-3), lastDone: day(-10), overdue: true },
    ],
    metrics: [
      { id: 'leads', label: 'Заявки', value: '6', change: '+2 к прошлой неделе', trend: 'up', better: 'up', series: [2, 3, 1, 4, 4, 6], period: 'за 7 дней' },
      { id: 'cpl', label: 'Стоимость заявки', value: '4 180 ₽', change: '−18 % к прошлой неделе', trend: 'down', better: 'down', series: [7400, 6900, 8100, 5600, 5100, 4180], period: 'за 7 дней' },
      { id: 'spend', label: 'Расход', value: '25 100 ₽', change: 'в пределах плана', trend: 'flat', better: 'down', series: [21000, 24000, 23500, 26000, 24800, 25100], period: 'за 7 дней' },
    ],
    terms: { price: rub(65000), contract: 'Договор № 12-2026, приложение 2', since: day(-28), period: 'month', nextInvoiceAt: day(26), until: day(10) },
  },
  {
    kind: 'fixed',
    id: 'land',
    clientId: 'karandash',
    linear: { name: 'Карандаш — лендинг домов', url: 'https://linear.app/rodion-ch/project/land' },
    key: 'LAND',
    title: 'Лендинг «Загородные дома»',
    summary: 'Отдельная страница под строительство загородных домов и рекламу на этот спрос.',
    health: 'on_track',
    now: 'Пишем тексты. Не хватает фото ещё одного построенного дома.',
    owner: people.manager,
    team: [people.manager, people.designer, people.dev],
    updatedAt: hoursAgo(7),
    finishBy: day(9),
    milestones: [
      { id: 'struct', title: 'Структура', state: 'done', due: day(-5) },
      { id: 'copy', title: 'Тексты и фото', state: 'current', due: day(3) },
      { id: 'go', title: 'Запуск', state: 'next', due: day(9) },
    ],
    terms: { price: rub(40000), since: day(-12) },
  },
];

// ---------------------------------------------------------------------------
// Tasks

const tasks: Task[] = [];
const counters: Record<string, number> = {};

function addTask(t: {
  projectId: string;
  group: string;
  title: string;
  status: TaskStatus;
  assignee: Person;
  description?: string;
  due?: number;
  age?: number;
  needsClient?: Task['needsClient'];
  attachments?: Task['attachments'];
  internal?: boolean;
}) {
  const p = projects.find((x) => x.id === t.projectId)!;
  counters[p.key] = (counters[p.key] ?? 0) + 1;
  const key = `${p.key}-${counters[p.key]}`;
  const age = t.age ?? 24 * (1 + rnd() * 20);
  tasks.push({
    id: key.toLowerCase(),
    key,
    projectId: t.projectId,
    clientVisible: !t.internal,
    linearUrl: `https://linear.app/rodion-ch/issue/MK42-${6000 + tasks.length}`,
    group: t.group,
    title: t.title,
    description: t.description ?? '',
    status: t.status,
    needsClient: t.needsClient,
    due: t.due === undefined ? undefined : day(t.due),
    assignee: t.assignee,
    threadId: `t-${key.toLowerCase()}`,
    comments: 0,
    unread: 0,
    attachments: t.attachments ?? [],
    createdAt: hoursAgo(age + 48),
    updatedAt: hoursAgo(age),
  });
  return key;
}

const S = (group: string, status: TaskStatus, assignee: Person, titles: string[], due?: number) =>
  titles.forEach((title, i) => addTask({ projectId: 'site', group, status, assignee, title, due: due === undefined ? undefined : due + i, age: status === 'done' ? 200 + i * 30 : undefined }));

S('Бриф', 'done', people.manager, ['Интервью с владельцем', 'Разбор конкурентов: 8 сайтов', 'Карта страниц', 'Цели и метрики сайта', 'Портрет клиента: дома и квартиры', 'Согласование брифа']);
addTask({
  projectId: 'site', group: 'Прототипы', status: 'review', assignee: people.designer, title: 'Прототип главной страницы', due: 2, age: 2.5,
  description: 'Квиз «Сколько стоит ремонт» на первом экране, блок «Как это было» с реальными объектами, отзывы из Яндекса, три оффера.\n\nВ версии 2 добавлен шаг квиза про бюджет — по вашему комментарию.',
  needsClient: { kind: 'approve', note: 'Утвердите прототип или оставьте замечания' },
  attachments: [{ id: 'd2', name: 'Прототип главной v2.pdf', sizeKb: 3120 }],
});
addTask({ projectId: 'site', group: 'Прототипы', status: 'in_progress', assignee: people.manager, title: 'Тексты «Как это было»: 6 объектов', due: 3, age: 26, description: 'Для каждого объекта: задача, срок, бюджет, фото до и после.', needsClient: { kind: 'answer', note: 'Какие два объекта можно показывать с адресом?' } });
S('Прототипы', 'in_progress', people.designer, ['Прототип страницы услуги', 'Прототип страницы «Цены»'], 3);
S('Прототипы', 'todo', people.designer, ['Прототип мобильной версии квиза', 'Прототип страницы объекта'], 4);
S('Прототипы', 'done', people.designer, ['Структура квиза: 7 шагов', 'Сценарий заявки из квиза в amoCRM', 'Навигация и подвал']);
S('Дизайн', 'backlog', people.designer, ['Дизайн главной', 'Дизайн страницы услуги', 'Дизайн квиза', 'UI-кит: кнопки, формы, карточки', 'Иконки этапов ремонта', 'Фотосъёмка объекта в Репино', 'Дизайн мобильной версии', 'Дизайн страницы «Цены»']);
S('Вёрстка и CMS', 'backlog', people.dev, ['Вёрстка главной', 'Квиз и отправка в amoCRM', 'Шаблон объекта в CMS', 'Перенос отзывов', 'Скорость: 90+ в PageSpeed', 'Цели Метрики и офлайн-конверсии']);
S('Запуск', 'backlog', people.manager, ['Перенос домена и редиректы', 'Проверка форм на проде', 'Обучение: как добавить объект', 'Запуск и наблюдение первую неделю']);

const A = (group: string, status: TaskStatus, assignee: Person, titles: string[], ageStart = 30) =>
  titles.forEach((title, i) => addTask({ projectId: 'ads', group, status, assignee, title, age: ageStart + i * 60 }));

addTask({ projectId: 'ads', group: 'Кампании', status: 'in_progress', assignee: people.ads, title: 'Кампания на загородные дома', due: 6, age: 9, description: 'Отдельная кампания под спрос на строительство домов, ведёт на новый лендинг.' });
addTask({ projectId: 'ads', group: 'Аналитика', status: 'todo', assignee: people.ads, title: 'Доступ к чату amoCRM для бота', age: 80, description: 'Чтобы заявки из чата попадали в отчёт, нужен доступ администратора к каналу.', needsClient: { kind: 'access', note: 'Передайте доступ администратора amoCRM через «Доступы»' } });
A('Отчёты', 'done', people.ads, ['Отчёт за прошлую неделю', 'Отчёт за позапрошлую неделю', 'Отчёт за третью неделю сентября', 'Отчёт за первую неделю сентября'], 22);
A('Кампании', 'done', people.ads, ['Минус-слова: 44 нецелевых запроса', 'Автотаргетинг ЕПК только на бренде', 'Ставки по времени суток', 'Объявления с ценой за м²', 'Остановка РСЯ без заявок'], 50);
A('Аналитика', 'done', people.ads, ['Цели Метрики на квиз и звонки', 'Выгрузка ClientID в amoCRM', 'Сверка заявок сентября'], 90);
A('Кампании', 'todo', people.ads, ['Тест оффера «Смета по плану»', 'Ретаргет на посетителей цен'], 20);
A('Аналитика', 'todo', people.ads, ['Офлайн-конверсии из amoCRM в Директ'], 40);

addTask({ projectId: 'land', group: 'Тексты и фото', status: 'in_progress', assignee: people.manager, title: 'Тексты лендинга', due: 2, age: 7, description: 'Оффер «Дом по проекту за 9 месяцев», этапы, цены от 4 000 ₽/м².' });
addTask({ projectId: 'land', group: 'Тексты и фото', status: 'todo', assignee: people.manager, title: 'Фото построенных домов', due: 2, age: 48, description: 'Нужны фото минимум двух домов: фасад, интерьер, процесс стройки.', needsClient: { kind: 'materials', note: 'Загрузите фото ещё одного дома' }, attachments: [{ id: 'd7', name: 'Фото объекта Репино.zip', sizeKb: 48200 }] });
addTask({ projectId: 'land', group: 'Тексты и фото', status: 'todo', assignee: people.designer, title: 'Схема этапов стройки', due: 3 });
['Структура лендинга', 'Анализ спроса на дома', 'Оффер и цены'].forEach((title, i) => addTask({ projectId: 'land', group: 'Структура', status: 'done', assignee: people.manager, title, age: 130 + i * 20 }));
['Вёрстка лендинга', 'Форма заявки в amoCRM', 'Публикация и цели'].forEach((title, i) => addTask({ projectId: 'land', group: 'Запуск', status: 'backlog', assignee: people.dev, title, due: 6 + i }));

// Studio-internal work: lives in Linear, never shown to the client.
addTask({ projectId: 'site', group: 'Вёрстка и CMS', status: 'in_progress', assignee: people.dev, title: 'Окружение и CI для темы', internal: true, age: 30 });
addTask({ projectId: 'site', group: 'Прототипы', status: 'todo', assignee: people.manager, title: 'Внутреннее ревью прототипов перед показом', internal: true, due: 1 });
addTask({ projectId: 'ads', group: 'Аналитика', status: 'todo', assignee: people.ads, title: 'Разметить заявки сентября для обучения классификатора', internal: true, age: 40 });

// ---------------------------------------------------------------------------
// Threads and messages

const messages: Message[] = [];
const threads: Thread[] = [];
/** Discussions opened by hand (`d-<n>`): the title and the task they are about. */
const discussions = new Map<string, { projectId: string; taskId?: string; title: string }>();
let msgId = 0;

function post(threadId: string, author: Person, body: string, at: string, attachments: Message['attachments'] = []) {
  messages.push({ id: `m${++msgId}`, threadId, author, body, at, attachments });
}

const studioLines = [
  'Обновили, посмотрите, пожалуйста.',
  'Готово, изменения внесли.',
  'Выложили новую версию в файлы задачи.',
  'Согласовали с разработкой, по срокам успеваем.',
  'Сделали, как договорились на созвоне.',
  'Проверили на мобильном, всё работает.',
];
const clientLines = ['Спасибо, посмотрю вечером.', 'Хорошо, согласен.', 'А можно сделать чуть крупнее?', 'Отлично, двигаемся дальше.', 'Уточню у прораба и напишу.', 'Да, так лучше.'];

const handWritten = new Set(['Прототип главной страницы', 'Тексты «Как это было»: 6 объектов']);

// A generic trail on tasks that saw some work.
for (const t of tasks) {
  if (t.status === 'backlog' || handWritten.has(t.title)) continue;
  const n = t.status === 'done' ? 2 + Math.floor(rnd() * 6) : 1 + Math.floor(rnd() * 4);
  const span = (NOW - new Date(t.createdAt).getTime()) / 3600_000;
  for (let i = 0; i < n; i++) {
    const studio = i % 2 === 0;
    const at = hoursAgo(span - ((span - (NOW - new Date(t.updatedAt).getTime()) / 3600_000) * (i + 1)) / n);
    post(t.threadId, studio ? t.assignee : people.me, studio ? pick(studioLines) : pick(clientLines), at);
  }
}

// Hand-written conversations where the demo tells its story.
const proto = tasks.find((t) => t.title === 'Прототип главной страницы')!;
post(proto.threadId, people.designer, 'Выложила первую версию прототипа главной. Квиз — на первом экране, ниже «Как это было».', hoursAgo(74));
post(proto.threadId, people.me, 'Квиз нравится. Можно добавить шаг про бюджет? Люди часто спрашивают про цену сразу.', hoursAgo(56));
post(proto.threadId, people.designer, 'Хорошая мысль, добавлю отдельным шагом с вилками.', hoursAgo(55));
post(proto.threadId, people.designer, 'Добавила шаг «Бюджет на ремонт» с вилками 1–3, 3–6 и 6+ млн ₽. Это версия 2 — посмотрите и утвердите, если всё хорошо.', hoursAgo(2.5), [{ id: 'd2', name: 'Прототип главной v2.pdf', sizeKb: 3120 }]);

const texts = tasks.find((t) => t.title.startsWith('Тексты «Как это было»'))!;
post(texts.threadId, people.manager, 'Собираем 6 объектов для «Как это было». Какие два можно показывать с адресом и фото фасада?', hoursAgo(26));

// Long project-level threads: this is what months of work look like.
const adsTopics = [
  ['Заявки за вчера: 2, обе из квиза.', people.ads],
  ['Почему выросла цена клика во вторник?', people.me],
  ['Конкуренты подняли ставки на «ремонт под ключ», перераспределили бюджет на районы.', people.ads],
  ['Звонок с рекламы был, но в amoCRM не попал.', people.me],
  ['Нашли: номер подменялся не на всех страницах. Поправили.', people.ads],
  ['Отчёт за неделю в файлах проекта.', people.ads],
  ['Можно больше заявок на дома?', people.me],
  ['Да, готовим отдельную кампанию и лендинг под дома.', people.ads],
  ['Бюджет на следующую неделю оставляем прежним?', people.ads],
  ['Да, оставляем.', people.me],
] as const;
for (let i = 0; i < 148; i++) {
  const [body, who] = adsTopics[i % adsTopics.length];
  post('t-ads', who, body, hoursAgo(28 * 24 - i * 4.4 - rnd() * 2));
}
post('t-ads', people.ads, 'Кампанию на дома запускаем в понедельник. Согласуете бюджет 15 000 ₽ в неделю сверх текущего?', hoursAgo(9));

const siteTopics = [
  ['Созвон по прототипам в четверг в 11:00 подходит?', people.manager],
  ['Да, подходит.', people.me],
  ['Итоги созвона: утверждаем структуру, добавляем раздел цен.', people.manager],
  ['Когда будет первый дизайн?', people.me],
  ['Через неделю после утверждения главной.', people.manager],
] as const;
for (let i = 0; i < 36; i++) {
  const [body, who] = siteTopics[i % siteTopics.length];
  post('t-site', who, body, hoursAgo(26 * 24 - i * 16));
}
post('t-land', people.manager, 'Структура лендинга готова, пишем тексты. Запуск через неделю с небольшим.', hoursAgo(120));
post('t-land', people.me, 'Отлично, фото второго дома пришлю в выходные.', hoursAgo(100));

messages.sort((a, b) => a.at.localeCompare(b.at));

// Unread: what the studio wrote after the client's last look.
const lastSeen: Record<string, string> = { [proto.threadId]: hoursAgo(50), [texts.threadId]: hoursAgo(30), 't-ads': hoursAgo(12) };

function rebuildThreads() {
  threads.length = 0;
  const byThread = new Map<string, Message[]>();
  for (const m of messages) byThread.set(m.threadId, [...(byThread.get(m.threadId) ?? []), m]);
  for (const [id, ms] of byThread) {
    const d = discussions.get(id);
    const task = d ? tasks.find((t) => t.id === d.taskId) : tasks.find((t) => t.threadId === id);
    const project = d ? projects.find((p) => p.id === d.projectId)! : task ? projects.find((p) => p.id === task.projectId)! : projects.find((p) => `t-${p.id}` === id)!;
    const last = ms.at(-1)!;
    const seen = lastSeen[id] ?? hoursAgo(-1);
    const unread = ms.filter((m) => m.author.side === 'studio' && m.at > seen).length;
    // Only fresh questions count: a week-old one has been answered on a call or is moot.
    const awaitingClient = last.author.side === 'studio' && /\?\s*$/.test(last.body) && last.at > hoursAgo(72);
    threads.push({
      id,
      projectId: project.id,
      taskId: task?.id,
      title: d ? d.title : task ? task.title : 'Общее по проекту',
      lastMessageAt: last.at,
      lastMessage: last.body,
      lastAuthor: last.author,
      unread,
      total: ms.length,
      awaitingClient,
    });
    if (task && !d) {
      task.comments = ms.length;
      task.unread = unread;
    }
  }
  threads.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
}
rebuildThreads();

function openDiscussion(input: NewDiscussion, author: Person): Thread {
  const id = `d-${discussions.size + 1}`;
  discussions.set(id, { projectId: input.projectId, taskId: input.taskId, title: input.title });
  post(id, author, input.body, nowIso());
  lastSeen[id] = author.side === 'client' ? nowIso() : new Date(Date.now() - 1000).toISOString();
  rebuildThreads();
  return clone(threads.find((t) => t.id === id)!);
}

// ---------------------------------------------------------------------------
// Money, documents, secrets

/** The BFF sends the project's title with the invoice; the mock does the same. */
const withProjectTitle = (i: Invoice): Invoice => ({ ...i, projectTitle: projects.find((p) => p.id === i.projectId)?.title });

/** Cancelled by the studio: out of the lists, but the client can still open one by its link. */
const cancelledInvoices: Invoice[] = [];

const invoices: Invoice[] = [
  {
    id: 'i3', number: '0003', issuedAt: day(0), dueAt: day(7), status: 'sent', total: rub(40000), paid: rub(0), projectId: 'land',
    lines: [{ title: 'Лендинг «Загородные дома»', note: 'Разработка под ключ, фиксированная стоимость', quantity: 1, price: rub(40000), total: rub(40000) }],
  },
  {
    id: 'i2', number: '0002', issuedAt: day(-4), dueAt: day(1), status: 'paid', total: rub(65000), paid: rub(65000), projectId: 'ads', actId: 'a1', actNumber: '0001', actDocumentId: 'd1',
    lines: [{ title: 'Реклама и аналитика', note: 'Сопровождение за месяц', quantity: 1, price: rub(65000), total: rub(65000) }],
    payment: { at: day(-3), method: 'transfer', reference: 'п/п № 481' },
  },
  {
    id: 'i1', number: '0001', issuedAt: day(-19), dueAt: day(-14), status: 'paid', total: rub(90000), paid: rub(90000), projectId: 'site',
    lines: [{ title: 'Новый сайт karandashnw.ru', note: 'Предоплата 50 % по договору № 12-2026', quantity: 1, price: rub(90000), total: rub(90000) }],
    payment: { at: day(-18), method: 'transfer', reference: 'п/п № 455' },
  },
];

const acts: Act[] = [
  { id: 'a1', number: '0001', issuedAt: day(-3), invoiceId: 'i2', invoiceIds: ['i2'], invoiceNumbers: ['0002'], total: rub(65000), status: 'to_sign', documentId: 'd1', fileName: 'Акт № 0001.pdf', lines: [], summary: '' },
];
/** The client an act or a draft invoice belongs to when it has no project (the demo has one client with a live portal). */
const actClient = (a: Act) => a.clientId ?? projects.find((p) => p.id === a.projectId)?.clientId ?? CLIENT_ID;
const ownActs = () => acts.filter((a) => actClient(a) === CLIENT_ID);
const draftClient = new Map<string, string>();

/** Applies an act form to a stored act; totals are computed here, as on the BFF. */
function fillAct(a: Act, input: Partial<ActInput>) {
  if (input.number) a.number = input.number;
  if (input.date !== undefined) a.issuedAt = input.date || day(0);
  if ('projectId' in input) a.projectId = input.projectId ?? null;
  if ('periodFrom' in input) a.periodFrom = input.periodFrom ?? null;
  if ('periodTo' in input) a.periodTo = input.periodTo ?? null;
  if (input.basis !== undefined) a.basis = input.basis;
  if (input.summary !== undefined) a.summary = input.summary;
  if (input.lines) {
    a.lines = input.lines.map((l) => ({ title: l.title, description: l.description || null, quantity: l.quantity, unit: l.unit, price: { amountMinor: l.price, currency: 'RUB' }, total: { amountMinor: Math.round(l.quantity * l.price), currency: 'RUB' }, source: l.source ?? null }));
    a.total = { amountMinor: a.lines.reduce((n, l) => n + l.total.amountMinor, 0), currency: 'RUB' };
  }
  if (input.invoiceIds) {
    a.invoiceIds = input.invoiceIds;
    a.invoiceNumbers = input.invoiceIds.map((i) => invoices.find((x) => x.id === i)?.number ?? i);
    a.invoiceId = input.invoiceIds[0] ?? null;
  }
}

/** Lines of a new invoice: free lines and, as before, catalog services; the total is computed here, as on the BFF. */
function invoiceLines(input: InvoiceInput): InvoiceLine[] {
  const lines: InvoiceLine[] = [
    ...(input.items ?? []).map(({ catalogId, note }) => {
      const c = must(catalog.find((x) => x.id === catalogId), 'Услуга');
      return { title: c.title, note: note || c.note, quantity: 1, unit: 'усл.', price: c.price, total: c.price };
    }),
    ...(input.lines ?? []).map((l) => ({ title: l.title, note: l.description || undefined, quantity: l.quantity, unit: l.unit, price: rubMinor(l.price), total: rubMinor(Math.round(l.quantity * l.price)) })),
  ];
  if (!lines.length) throw new Error('Добавьте в счёт хотя бы одну строку');
  return lines;
}

const subscriptions: Subscription[] = [
  { id: 'sub1', title: 'Реклама и аналитика', price: rub(65000), period: 'month', status: 'active', nextInvoiceAt: day(26), since: day(-28), projectId: 'ads' },
];

const doc = (id: string, name: string, kind: DocumentRef['kind'], by: Person, h: number, sizeKb: number, projectId?: string): DocumentRef => ({
  id, name, kind, uploadedBy: by, uploadedAt: hoursAgo(h), sizeKb, projectId,
});

const documents: DocumentRef[] = [
  doc('d2', 'Прототип главной v2.pdf', 'design', people.designer, 2.5, 3120, 'site'),
  doc('d4', 'Отчёт по рекламе за прошлую неделю.pdf', 'report', people.ads, 33, 642, 'ads'),
  doc('d7', 'Фото объекта Репино.zip', 'other', people.me, 48, 48200, 'land'),
  doc('d6', 'Структура лендинга.pdf', 'design', people.designer, 120, 880, 'land'),
  doc('d1', 'Бриф — новый сайт.pdf', 'brief', people.manager, 17 * 24, 214, 'site'),
  doc('d3', 'Договор № 12-2026.pdf', 'contract', people.manager, 28 * 24, 188),
  doc('d8', 'Отчёт по рекламе, 2 недели назад.pdf', 'report', people.ads, 8 * 24, 610, 'ads'),
  doc('d9', 'Отчёт по рекламе, 3 недели назад.pdf', 'report', people.ads, 15 * 24, 598, 'ads'),
];

type StoredSecret = Secret & { sealed?: SealedSecret };

const secrets: StoredSecret[] = [
  { id: 'sec2', title: 'Стейджинг нового сайта — вход в админку', from: people.manager, to: people.me, projectId: 'site', createdAt: hoursAgo(4), expiresAt: hoursAgo(-7 * 24), status: 'waiting' },
  { id: 'sec1', title: 'Хостинг Beget — панель управления', from: people.me, to: people.manager, projectId: 'site', createdAt: hoursAgo(14 * 24), expiresAt: hoursAgo(13 * 24), status: 'opened', openedAt: hoursAgo(14 * 24 - 0.2) },
];

// The seeded secret gets a real ciphertext so the demo can open it once.
const seeded = encryptSecret('Адрес: https://stage.karandashnw.ru/wp-admin\nЛогин: client\nПароль: демо-пароль-для-стейджинга').then((sealed) => {
  secrets[0].sealed = sealed;
});

/** Ids of secrets the addressee asked to be sent again (the demo has no studio inbox to show it in). */
export const rerequested: string[] = [];

const extraActivity: Activity[] = [];

// ---------------------------------------------------------------------------

const wait = (ms = 160) => new Promise((r) => setTimeout(r, ms));
const clone = <T,>(v: T): T => structuredClone(v);
const must = <T,>(v: T | undefined, what: string): T => {
  if (!v) throw new Error(`${what} не найден`);
  return v;
};
const publicSecret = (stored: StoredSecret): Secret => {
  const { sealed, ...s } = stored;
  void sealed;
  return s;
};
const nowIso = () => new Date().toISOString();
/** A stand-in for the SHA-256 of a file in the demo: 64 hex digits, stable per input. */
const fakeSha256 = (seed: string) => {
  let h = 0;
  let out = '';
  for (let i = 0; out.length < 64; i++) {
    h = (Math.imul(h, 31) + seed.charCodeAt(i % seed.length) + i) | 0;
    out += (h >>> 0).toString(16).padStart(8, '0');
  }
  return out.slice(0, 64);
};

const nextActNumber = () => `${new Date().getFullYear()}-${String(Math.max(0, ...acts.map((a) => Number(/^\d{4}-(\d+)$/.exec(a.number)?.[1] ?? 0))) + 1).padStart(4, '0')}`;

function markPaid(inv: Invoice, method: PaymentMethod, reference: string) {
  inv.status = 'paid';
  inv.paid = inv.total;
  inv.payment = { at: day(0), method, reference, receipt: method === 'transfer' ? undefined : 'Чек отправлен на почту' };
  // Paying leaves the studio a DRAFT act to review and send: the client sees nothing until then.
  const number = nextActNumber();
  const lines = inv.lines.map((l) => ({ title: l.title, description: l.note ?? null, quantity: l.quantity, unit: l.unit ?? 'усл.', price: l.price, total: l.total, source: `invoice:${inv.id}` }));
  const act: Act = { id: `a${acts.length + 1}`, number, issuedAt: day(0), invoiceId: inv.id, invoiceIds: [inv.id], invoiceNumbers: [inv.number], total: inv.total, status: 'draft', fileName: `Акт № ${number}.pdf`, lines, summary: '', projectId: inv.projectId, basis: `Счёт № ${inv.number} от ${inv.issuedAt}` };
  acts.unshift(act);
  inv.actId = act.id;
  inv.actNumber = number;
  inv.actStatus = 'draft';
  extraActivity.push({ id: `x${extraActivity.length}`, projectId: inv.projectId ?? '', at: nowIso(), who: people.me, text: `Оплачен счёт № ${inv.number}`, kind: 'money' });
}

const vis = () => tasks.filter((t) => t.clientVisible);
const visThreads = () => threads.filter((t) => !t.taskId || tasks.find((x) => x.id === t.taskId)?.clientVisible);

const baseClient = {
  async workspace() {
    await wait();
    return clone({ ...workspace, apiEnabled: !!karandash().apiEnabled });
  },

  async actions() {
    await wait();
    const items: ActionItem[] = [];
    vis().filter((t) => t.needsClient && t.status !== 'done').forEach((task) => items.push({ kind: 'task', task: clone(task) }));
    const listed = new Set(items.map((i) => (i.kind === 'task' ? i.task.threadId : '')));
    visThreads().filter((t) => t.awaitingClient && !listed.has(t.id)).forEach((thread) => items.push({ kind: 'reply', thread: clone(thread) }));
    invoices.filter((i) => i.status === 'sent' || i.status === 'overdue').forEach((invoice) => items.push({ kind: 'invoice', invoice: clone(invoice) }));
    ownActs().filter((a) => a.status === 'to_sign').forEach((act) => items.push({ kind: 'act', act: clone(act) }));
    secrets.filter((s) => s.to.id === workspace.me.id && s.status === 'waiting').forEach((s) => items.push({ kind: 'secret', secret: publicSecret(s) }));
    return items;
  },

  async search(q) {
    await wait(80);
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    const hits: SearchHit[] = [];
    const proj = (id?: string) => projects.find((p) => p.id === id)?.title ?? '';
    vis()
      .filter((t) => `${t.key} ${t.title}`.toLowerCase().includes(s))
      .slice(0, 8)
      .forEach((t) => hits.push({ kind: 'task', id: t.id, title: t.title, hint: `${t.key}, ${proj(t.projectId)}`, to: `/p/${t.projectId}/tasks/${t.id}` }));
    const seen = new Set<string>();
    [...messages]
      .reverse()
      .filter((m) => m.body.toLowerCase().includes(s))
      .forEach((m) => {
        const t = visThreads().find((x) => x.id === m.threadId);
        if (!t || seen.has(m.threadId) || seen.size >= 6) return;
        seen.add(m.threadId);
        hits.push({ kind: 'thread', id: t.id, title: m.body, hint: `${t.title}, ${proj(t.projectId)}`, to: `/p/${t.projectId}/talk/${t.id}` });
      });
    documents
      .filter((d) => d.name.toLowerCase().includes(s))
      .slice(0, 5)
      .forEach((d) => hits.push({ kind: 'document', id: d.id, title: d.name, hint: proj(d.projectId) || 'Общие документы', to: d.projectId ? `/p/${d.projectId}/files` : '/documents' }));
    invoices
      .filter((i) => `счёт ${i.number} ${i.lines[0]?.title}`.toLowerCase().includes(s))
      .forEach((i) => hits.push({ kind: 'invoice', id: i.id, title: `Счёт № ${i.number}`, hint: i.lines[0]?.title ?? '', to: `/money/${i.id}` }));
    return hits;
  },

  async projects() {
    await wait();
    return clone(projects.filter((p) => p.clientId === CLIENT_ID));
  },
  async project(id) {
    await wait();
    return clone(must(projects.find((p) => p.id === id), 'Проект'));
  },
  async activity(projectId) {
    await wait();
    const items: Activity[] = [];
    for (const t of vis().filter((x) => x.projectId === projectId)) {
      if (t.status === 'done') items.push({ id: `a-${t.id}`, projectId, at: t.updatedAt, who: t.assignee, text: t.title, taskId: t.id, kind: 'done' });
      else if (t.status === 'review') items.push({ id: `a-${t.id}`, projectId, at: t.updatedAt, who: t.assignee, text: `${t.title} — на согласовании`, taskId: t.id, kind: 'status' });
    }
    for (const d of documents.filter((x) => x.projectId === projectId)) items.push({ id: `f-${d.id}`, projectId, at: d.uploadedAt, who: d.uploadedBy, text: d.name, kind: 'file' });
    items.push(...extraActivity.filter((a) => a.projectId === projectId));
    return clone(items.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 10));
  },
  async tasks(projectId) {
    await wait();
    return clone(projectId ? vis().filter((t) => t.projectId === projectId) : vis());
  },
  async task(id) {
    await wait();
    return clone(must(vis().find((t) => t.id === id || t.key === id), 'Задача'));
  },
  async approveTask(id) {
    await wait(260);
    const t = must(vis().find((x) => x.id === id), 'Задача');
    t.status = 'done';
    t.needsClient = undefined;
    t.updatedAt = nowIso();
    post(t.threadId, workspace.me, 'Утверждаю.', t.updatedAt);
    lastSeen[t.threadId] = t.updatedAt;
    const p = projects.find((x) => x.id === t.projectId)!;
    if (!vis().some((x) => x.projectId === p.id && x.needsClient)) p.health = 'on_track';
    rebuildThreads();
    return clone(t);
  },

  async threads(projectId) {
    await wait();
    return clone(projectId ? visThreads().filter((t) => t.projectId === projectId) : visThreads());
  },
  async messages(threadId, opts) {
    await wait();
    const all = messages.filter((m) => m.threadId === threadId);
    const limit = opts?.limit ?? 30;
    const end = opts?.before ? all.findIndex((m) => m.id === opts.before) : all.length;
    const start = Math.max(0, end - limit);
    return clone({ items: all.slice(start, end), hasEarlier: start > 0, total: all.length });
  },
  async sendMessage(threadId, body, documentIds = []) {
    await wait(100);
    const files = documents.filter((d) => documentIds.includes(d.id)).map((d) => ({ id: d.id, name: d.name, sizeKb: d.sizeKb }));
    post(threadId, workspace.me, body, nowIso(), files);
    lastSeen[threadId] = nowIso();
    rebuildThreads();
    return clone(messages.at(-1)!);
  },
  async createThread(input) {
    await wait(100);
    must(projects.find((p) => p.id === input.projectId), 'Проект');
    if (input.taskId) must(vis().find((t) => t.id === input.taskId && t.projectId === input.projectId), 'Задача');
    return openDiscussion(input, workspace.me);
  },
  async markRead(threadId) {
    lastSeen[threadId] = nowIso();
    rebuildThreads();
  },

  async invoices() {
    await wait();
    return clone(invoices.filter((i) => i.status !== 'draft').map(withProjectTitle));
  },
  async invoice(id) {
    await wait();
    // A cancelled invoice still opens (read-only) for the one it was issued to; it is in no list. A draft never does.
    const found = invoices.find((i) => (i.id === id || i.number === id) && i.status !== 'draft') ?? cancelledInvoices.find((i) => i.id === id || i.number === id);
    if (!found) throw new ApiError(404, 'Счёт не найден');
    return clone(withProjectTitle(found));
  },
  async startCheckout(invoiceId, method): Promise<CheckoutSession> {
    await wait(400);
    const inv = must(invoices.find((i) => i.id === invoiceId), 'Счёт');
    if (inv.status === 'paid') throw new Error('Счёт уже оплачен');
    return {
      id: `cs_${method}_${invoiceId}_${Date.now()}`,
      invoiceId,
      method,
      amount: inv.total,
      status: 'pending',
      qrPayload: method === 'sbp' ? `https://qr.nspk.ru/DEMO${invoiceId.toUpperCase()}?type=02&sum=${inv.total.amountMinor}&cur=RUB` : undefined,
    };
  },
  async checkoutStatus(sessionId) {
    await wait(1400);
    const [, m, invoiceId] = sessionId.split('_');
    const method = m === 'sbp' ? 'sbp' : 'card';
    const inv = must(invoices.find((i) => i.id === invoiceId), 'Счёт');
    if (inv.status !== 'paid') markPaid(inv, method, `операция ${sessionId.slice(-6)}`);
    return { checkout: { id: sessionId, invoiceId, method, amount: inv.total, status: 'paid' }, invoice: clone(inv) };
  },
  async invoicePdf() {
    await wait();
    return new Blob(['%PDF-1.4 demo'], { type: 'application/pdf' });
  },
  async downloadReportPdf() {
    await wait();
    return new Blob(['%PDF-1.4 demo report'], { type: 'application/pdf' });
  },
  async downloadDocument() {
    await wait();
    return new Blob(['demo'], { type: 'application/octet-stream' });
  },
  async actFile() {
    await wait();
    return new Blob(['%PDF-1.4 demo act'], { type: 'application/pdf' });
  },
  async reportTransfer(invoiceId) {
    await wait(300);
    const inv = must(invoices.find((i) => i.id === invoiceId), 'Счёт');
    inv.status = 'processing';
    // In the demo the bank statement "arrives" a few seconds later.
    setTimeout(() => {
      if (inv.status === 'processing') markPaid(inv, 'transfer', 'по выписке банка');
    }, 8000);
    return clone(inv);
  },
  async acts() {
    await wait();
    // What the studio has sent: a draft is its own, a void act is gone.
    return clone(ownActs().filter((a) => a.status === 'to_sign' || a.status === 'signed'));
  },
  async signAct(id, input) {
    await wait(260);
    const a = must(ownActs().find((x) => x.id === id && (x.status === 'to_sign' || x.status === 'signed')), 'Акт');
    if (a.status !== 'to_sign') throw new Error('Акт уже подписан');
    if (!input.name.trim()) throw new Error('Введите имя и фамилию');
    if (!input.consent) throw new Error('Подтвердите, что вы ознакомились с актом и подписываете его');
    a.status = 'signed';
    a.signedAt = nowIso();
    a.signedBy = people.me.id;
    a.signerName = people.me.name;
    a.signedName = input.name.trim();
    a.signedHash = fakeSha256(a.number);
    a.signedVia = 'portal';
    return clone(a);
  },
  async uploadActScan(id, file) {
    await wait(300);
    const a = must(ownActs().find((x) => x.id === id && x.status === 'to_sign'), 'Акт');
    const d: DocumentRef = { id: `d${documents.length + 100}`, name: `Скан акта № ${a.number}.${file.name.split('.').pop() ?? 'pdf'}`, kind: 'act_scan', projectId: a.projectId ?? undefined, uploadedBy: people.me, uploadedAt: nowIso(), sizeKb: Math.max(1, Math.round(file.size / 1024)) };
    documents.unshift(d);
    Object.assign(a, { status: 'signed', signedAt: nowIso(), signedBy: people.me.id, signerName: people.me.name, signedName: null, signedHash: fakeSha256(`scan-${a.number}`), signedVia: 'scan', scanDocumentId: d.id });
    return clone(a);
  },
  async subscriptions() {
    await wait();
    return clone(subscriptions);
  },

  async documents(projectId) {
    await wait();
    return clone(projectId ? documents.filter((d) => d.projectId === projectId) : documents);
  },

  async secrets() {
    await wait();
    await seeded;
    return secrets.map(publicSecret).map(clone);
  },
  async sendSecret({ title, toId, projectId, onboardingId, value, ttlHours }) {
    const sealed = await encryptSecret(value);
    await wait(220);
    const now = new Date();
    const s: StoredSecret = {
      id: `sec${secrets.length + 1}`,
      title,
      from: workspace.me,
      to: must(workspace.people.find((p) => p.id === toId), 'Получатель'),
      projectId,
      createdAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + ttlHours * 3600_000).toISOString(),
      status: 'waiting',
      sealed,
    };
    secrets.unshift(s);
    // A secret that answers an access request closes it, as the BFF does.
    const asked = onboarding.find((o) => o.kind === 'access' && !o.done && (o.id === onboardingId || (!onboardingId && projectId && o.projectId === projectId && o.title.toLowerCase() === title.trim().toLowerCase())));
    if (asked) Object.assign(asked, { done: true, doneAt: nowIso(), secretId: s.id });
    return publicSecret(s);
  },
  async openSecret(id) {
    await seeded;
    await wait(220);
    const s = must(secrets.find((x) => x.id === id), 'Доступ');
    if (s.to.id !== workspace.me.id) throw new Error('Этот доступ адресован другому человеку');
    if (s.status !== 'waiting' || !s.sealed) throw new Error('Доступ уже открыт или больше недействителен');
    const value = await decryptSecret(s.sealed);
    s.sealed = undefined;
    s.status = 'opened';
    s.openedAt = nowIso();
    return value;
  },
  async revokeSecret(id) {
    await wait(180);
    const s = must(secrets.find((x) => x.id === id), 'Доступ');
    s.sealed = undefined;
    s.status = 'revoked';
    return publicSecret(s);
  },
  async rerequestSecret(id) {
    await wait(180);
    const s = must(secrets.find((x) => x.id === id), 'Доступ');
    if (s.to.id !== workspace.me.id) throw new Error('Этот доступ адресован другому человеку');
    if (s.status === 'waiting' && new Date(s.expiresAt) > new Date()) throw new Error('Доступ ещё можно открыть, повторный запрос не нужен');
    rerequested.push(id);
  },
} satisfies Partial<PortalApi>;

// ---------------------------------------------------------------------------
// Studio side

// A reply from the client that the studio still owes an answer to.
post('t-land', people.me, 'А можно на лендинге показать цену за дом «под ключ», а не только за м²?', hoursAgo(20));
messages.sort((a, b) => a.at.localeCompare(b.at));
rebuildThreads();

const studioTeam: Person[] = [people.manager, people.designer, people.dev, people.ads];

// Access administration (MK42-5783): who may do what, and the audit log.
const studioRoleTitle: Record<StudioRole, string> = { owner: 'Владелец студии', manager: 'Менеджер проектов', specialist: 'Специалист' };
const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
const demoRoles: Record<string, StudioRole> = { 'u-pm': 'owner', 'u-ads': 'manager' };
const access = new Map<string, PersonAccess>(
  studioTeam.map((p, i) => [
    p.id,
    {
      person: p,
      email: `${p.id.slice(2)}@mark-42.example`,
      side: 'studio',
      studioRole: demoRoles[p.id] ?? 'specialist',
      clientRole: null,
      clientId: null,
      clientName: null,
      invited: 'accepted',
      lastSeen: hoursAgo(1 + i * 7),
      disabled: false,
    },
  ]),
);
const auditLog: AuditEvent[] = [];
let auditSeq = 0;
function filterAudit(f: AuditFilter = {}): AuditEvent[] {
  const lo = f.since ? new Date(f.since).getTime() : -Infinity;
  // A bare date covers that whole day, as on the server.
  const hi = f.until ? new Date(f.until).getTime() + (f.until.length <= 10 ? 86_400_000 : 1) : Infinity;
  return auditLog.filter(
    (e) =>
      (!f.actor || e.actor?.id === f.actor) &&
      (!f.clientId || e.clientId === f.clientId) &&
      (!f.action || e.action.startsWith(f.action)) &&
      new Date(e.at).getTime() >= lo &&
      new Date(e.at).getTime() < hi,
  );
}
function log(action: AuditAction, target: Person | null, data: Record<string, unknown> = {}, actor: Person | null = people.manager, clientName: string | null = null) {
  // The demo has one client with a log; its id is looked up by name because clients are declared below.
  auditLog.unshift({ id: ++auditSeq, at: nowIso(), action, actor, target, clientId: clientName ? 'karandash' : null, clientName, data });
}
log('login', people.ads, {}, people.ads);
log('login', people.me, {}, people.me, 'Карандаш');
log('secret_opened', people.me, { title: 'Стейджинг нового сайта — вход в админку' }, people.me, 'Карандаш');

const clientPerson = (id: string, name: string, role: string): Person => ({
  id,
  name,
  role,
  side: 'client',
  initials: name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase(),
});

const clients: Client[] = [
  {
    id: 'karandash',
    name: 'Карандаш',
    legalName: 'ООО «Карандаш» (демо)',
    taxId: '7800000000',
    legal: { legalName: 'ООО «Карандаш» (демо)', taxId: '7800000000', kpp: '780001001', ogrn: '', address: 'г. Санкт-Петербург, демо-адрес' },
    contour: 'ru',
    stage: 'active',
    apiEnabled: true,
    since: day(-28),
    manager: people.manager,
    contacts: [
      { person: people.me, email: 'owner@karandash.example', role: 'owner', invited: 'accepted' },
      { person: people.accountant, email: 'buh@karandash.example', role: 'accountant', invited: 'accepted' },
    ],
  },
  {
    id: 'apart',
    name: 'ApartCompany',
    legalName: 'ИП Ларин Д. (демо)',
    taxId: '780000000000',
    legal: { legalName: 'ИП Ларин Д. (демо)', taxId: '780000000000', kpp: '', ogrn: '', address: '' },
    contour: 'ru',
    stage: 'proposal',
    since: day(-29),
    manager: people.manager,
    contacts: [{ person: clientPerson('u-apart', 'Дмитрий Ларин', 'Владелец, ApartCompany'), email: 'dl@apart.example', role: 'owner', invited: 'pending' }],
  },
];

// Payers: the legal entities a client's invoices are made out to, each its own buyer (portal-bff payers.py).
type MockPayer = { id: string; clientId: string; legal: LegalValues; email: string; isDefault: boolean };
const mockPayers: MockPayer[] = [
  { id: 'karandash', clientId: 'karandash', legal: { ...clients[0].legal }, email: 'buh@karandash.example', isDefault: true },
  // An organisation's ИНН without КПП: the demo shows the «проверьте реквизиты» prompt on «Реклама».
  { id: 'pay-planb', clientId: 'karandash', legal: { legalName: 'ООО «ПЛАН Б» (демо)', taxId: '7800000001', kpp: '', ogrn: '', address: '' }, email: '', isDefault: false },
];

/** What an invoice should carry and the payer lacks: required fields, КПП of an organisation (10-digit ИНН). */
export const payerMissing = (v: LegalValues) => [...(v.legalName?.trim() ? [] : ['Название организации или ИП']), ...(v.taxId?.trim() ? [] : ['ИНН']), ...(/^\d{10}$/.test(v.taxId ?? '') && !v.kpp?.trim() ? ['КПП'] : [])];

function payersOf(clientId: string): MockPayer[] {
  if (!mockPayers.some((p) => p.clientId === clientId)) {
    const c = must(clients.find((x) => x.id === clientId), 'Клиент');
    mockPayers.push({ id: clientId, clientId, legal: { ...c.legal }, email: '', isDefault: true });
  }
  return mockPayers.filter((p) => p.clientId === clientId).sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

const projectPayerId = (p: Project) => p.payerId ?? payersOf(p.clientId)[0].id;

function payerSummary(p: MockPayer): PayerSummary {
  return {
    id: p.id,
    legal: clone(p.legal),
    legalName: p.legal.legalName ?? '',
    taxId: p.legal.taxId ?? '',
    isDefault: p.isDefault,
    missing: payerMissing(p.legal),
    projects: projects.filter((x) => x.clientId === p.clientId && projectPayerId(x) === p.id).map((x) => ({ id: x.id, title: x.title })),
  };
}

const payerSummaries = (clientId: string) => payersOf(clientId).map(payerSummary);

function mustPayer(clientId: string, id: string) {
  return must(
    payersOf(clientId).find((p) => p.id === id),
    'Плательщик',
  );
}

/** The default payer into the client's own requisites, as the BFF mirrors it. */
function mirrorDefault(clientId: string) {
  const c = clients.find((x) => x.id === clientId);
  const d = payersOf(clientId)[0];
  if (c) Object.assign(c, { legal: { ...d.legal }, legalName: d.legal.legalName ?? '', taxId: d.legal.taxId ?? '' });
}

function makeDefaultPayer(clientId: string, id: string) {
  mustPayer(clientId, id);
  for (const p of payersOf(clientId)) p.isDefault = p.id === id;
  mirrorDefault(clientId);
}

/** Payers of active projects whose requisites lack what an invoice should carry. */
const incompletePayers = (clientId: string) =>
  payersOf(clientId)
    .map(payerSummary)
    .map((s) => ({ ...s, projects: s.projects.filter((x) => !['paused', 'done'].includes(projects.find((p) => p.id === x.id)?.health ?? '')) }))
    .filter((s) => s.projects.length && s.missing.length);

const payerPrompt = (s: PayerSummary) => `Проверьте реквизиты для счетов: не указан ${s.missing.join(', ')} у ${s.legalName || 'плательщика'}`;

const invoicePayer = (id: string, clientId = CLIENT_ID): InvoicePayer => {
  const p = mustPayer(clientId, id);
  return { id: p.id, name: p.legal.legalName ?? '' };
};

projects.filter((p) => p.id === 'ads').forEach((p) => (p.payerId = 'pay-planb'));
invoices.forEach((i) => (i.payer = invoicePayer(i.projectId === 'ads' ? 'pay-planb' : 'karandash')));


const catalog: CatalogItem[] = [
  { id: 'c-retainer', title: 'Реклама и аналитика', note: 'Сопровождение за месяц', price: rub(65000), kind: 'subscription' },
  { id: 'c-landing', title: 'Лендинг под ключ', note: 'Разработка, фиксированная стоимость', price: rub(40000), kind: 'fixed' },
  { id: 'c-site', title: 'Сайт под ключ', note: 'Этап по договору', price: rub(90000), kind: 'fixed' },
  { id: 'c-audit', title: 'Аудит рекламы и сайта', note: 'Разовая работа', price: rub(12000), kind: 'fixed' },
];

const clientName = (projectId: string) => clients.find((c) => c.id === projects.find((p) => p.id === projectId)?.clientId)?.name ?? '';
// A client's last word needs an answer when it asks something. Live: this is a semantic
// judgment (TypeSafe Choice «нужен ли ответ»), the regex only stands in for the demo.
const closedAt: Record<string, string> = {};
// «Ждёт нас»: the client has the last word and no one in the studio answered, «?» or not.
const needsAnswer = (m?: Message) => !!m && m.author.side === 'client' && !(closedAt[m.threadId] && closedAt[m.threadId] >= m.at);
const waitedHours = (m: Message) => Math.max(0, Math.floor((Date.now() - new Date(m.at).getTime()) / 3600_000));

const lastByThread = () => {
  const m = new Map<string, Message>();
  for (const x of messages) m.set(x.threadId, x);
  return m;
};

const withWaiting = (t: Thread, m?: Message): StudioThread => ({
  ...t,
  client: clientName(t.projectId),
  clientId: projects.find((p) => p.id === t.projectId)?.clientId ?? '',
  waitingOnUs: needsAnswer(m),
  waitingHours: needsAnswer(m) ? waitedHours(m!) : null,
  closed: !!m && m.author.side === 'client' && !needsAnswer(m),
});

const baseStudio = {
  async overview() {
    await wait();
    const last = lastByThread();
    const attention: StudioAttention[] = [];
    for (const t of threads) {
      const m = last.get(t.id);
      if (needsAnswer(m)) attention.push({ kind: 'reply', thread: clone(withWaiting(t, m)), client: clientName(t.projectId), waitingHours: waitedHours(m!) });
    }
    for (const inv of invoices.filter((i) => i.status === 'overdue')) attention.push({ kind: 'overdue', invoice: clone(inv), client: clientName(inv.projectId ?? '') });
    for (const t of tasks.filter((x) => x.needsClient && x.status !== 'done')) {
      const days = Math.floor((Date.now() - new Date(t.updatedAt).getTime()) / 86_400_000);
      if (days >= 2) attention.push({ kind: 'stale_client', task: clone(t), client: clientName(t.projectId), days });
    }
    for (const p of projects) {
      const days = Math.floor((Date.now() - new Date(p.updatedAt).getTime()) / 86_400_000);
      if (days >= 5 && p.health !== 'paused' && p.health !== 'done') attention.push({ kind: 'now_outdated', project: clone(p), client: clientName(p.id), days });
    }
    for (const p of projects) {
      if (p.kind !== 'retainer' || p.health === 'paused' || p.health === 'done') continue;
      for (const r of p.rhythm) {
        const late = r.next ? Math.round((Date.now() - new Date(r.next).getTime()) / 86_400_000) : 0;
        if (r.overdue && r.next && late > 1) attention.push({ kind: 'rhythm', project: { id: p.id, title: p.title }, client: clientName(p.id), rhythm: { id: r.id, title: r.title, next: r.next }, days: late });
      }
    }
    for (const c of clients) incompletePayers(c.id).forEach((payer) => attention.push({ kind: 'payer_incomplete', payer, client: c.name, clientId: c.id }));
    attention.push(...webhookAttention());
    const month = day(0).slice(0, 7);
    const revenue = invoices.filter((i) => i.status === 'paid' && i.payment?.at.startsWith(month)).reduce((n, i) => n + i.paid.amountMinor, 0);
    const receivable = invoices.filter((i) => i.status === 'sent' || i.status === 'overdue' || i.status === 'processing').reduce((n, i) => n + i.total.amountMinor - i.paid.amountMinor, 0);
    return {
      attention: sortAttention(attention),
      revenueMonth: { amountMinor: revenue, currency: 'RUB' },
      receivable: { amountMinor: receivable, currency: 'RUB' },
      activeClients: clients.filter((c) => c.stage === 'active').length,
      openTasks: tasks.filter((t) => t.status !== 'done').length,
    };
  },
  async integrationsHealth(): Promise<IntegrationsHealth> {
    await wait();
    const ago = (min: number) => new Date(Date.now() - min * 60_000).toISOString();
    return {
      generatedAt: new Date().toISOString(),
      dependencies: [
        { key: 'invoiceninja', name: 'Invoice Ninja', status: 'ok', lastOkAt: ago(1), lastErrorAt: null, error: null },
        { key: 'linear', name: 'Linear', status: 'ok', lastOkAt: ago(2), lastErrorAt: null, error: null },
        { key: 'mail', name: 'Почта', status: 'ok', lastOkAt: ago(40), lastErrorAt: null, error: null },
        { key: 'payments', name: 'Платежи', status: 'ok', lastOkAt: ago(1), lastErrorAt: null, error: null, provider: 'paddle', mode: 'sandbox' },
        { key: 'worker', name: 'Фоновый цикл', status: 'ok', lastOkAt: ago(1), lastErrorAt: null, error: null },
        { key: 'erp', name: 'ERP (LiveOS)', status: 'ok', lastOkAt: ago(12), lastErrorAt: null, error: null, caller: 'liveos' },
      ],
    };
  },
  async team(): Promise<TeamMember[]> {
    const seen = studioTeam.map((p) => access.get(p.id)!);
    return clone(studioTeam.map((p, i): TeamMember => ({ ...p, studioRole: seen[i].studioRole!, email: seen[i].email, invited: seen[i].invited, lastSeen: seen[i].lastSeen, disabled: seen[i].disabled })));
  },
  async inviteTeammate({ name, email, studioRole }: { name: string; email: string; studioRole: StudioRole }): Promise<TeamMember[]> {
    await wait(200);
    const mail = email.trim().toLowerCase();
    if ([...access.values()].some((a) => a.email === mail)) throw new Error('Этот человек уже в кабинете');
    const person: Person = { id: `u-${Date.now()}`, name: name.trim(), role: studioRoleTitle[studioRole], side: 'studio', initials: initialsOf(name) };
    studioTeam.push(person);
    access.set(person.id, { person, email: mail, side: 'studio', studioRole, clientRole: null, clientId: null, clientName: null, invited: 'pending', lastSeen: null, disabled: false });
    log('invite', person);
    return baseStudio.team();
  },
  async updatePerson(id: string, patch: PersonPatch) {
    await wait(150);
    const a = must(access.get(id), 'Человек');
    if (patch.name !== undefined || patch.email !== undefined) {
      const email = patch.email?.trim().toLowerCase();
      if (email !== undefined && (!/^[^@\s]+@[^@\s]+$/.test(email) || email.length > 254)) throw new Error('Проверьте адрес почты');
      if (email && email !== a.email && [...access.values()].some((x) => x.email === email)) throw new Error('Эта почта уже занята другим человеком');
      if (patch.name !== undefined && (!patch.name.trim() || patch.name.length > 200)) throw new Error('Имя: от 1 до 200 символов');
      if (patch.name !== undefined) a.person.name = patch.name.trim();
      if (email) a.email = email;
      log('person_edited', a.person);
    }
    const owners = [...access.values()].filter((x) => x.side === 'studio' && x.studioRole === 'owner' && !x.disabled && x.person.id !== id).length;
    const losingOwner = a.studioRole === 'owner' && ((patch.studioRole && patch.studioRole !== 'owner') || (patch.disabled && !a.disabled));
    if (losingOwner && owners === 0) throw new Error('В студии должен остаться хотя бы один владелец');
    if (patch.studioRole && a.side !== 'studio') throw new Error('Роль студии — только у людей студии');
    if (patch.clientRole && a.side !== 'client') throw new Error('Роль клиента — только у людей клиента');
    if (patch.studioRole && patch.studioRole !== a.studioRole) {
      log('role_changed', a.person, { side: 'studio', before: a.studioRole, after: patch.studioRole });
      a.studioRole = patch.studioRole;
    }
    if (patch.clientRole && patch.clientRole !== a.clientRole) {
      log('role_changed', a.person, { side: 'client', before: a.clientRole, after: patch.clientRole });
      a.clientRole = patch.clientRole;
      const contact = clients.flatMap((c) => c.contacts).find((x) => x.person.id === id);
      if (contact) contact.role = patch.clientRole;
    }
    if (patch.disabled !== undefined && patch.disabled !== a.disabled) {
      a.disabled = patch.disabled;
      if (a.disabled) a.lastSeen = null;
      log(a.disabled ? 'disabled' : 'enabled', a.person);
    }
    return clone(a);
  },
  async revokeSessions(id: string) {
    await wait(150);
    const a = must(access.get(id), 'Человек');
    a.lastSeen = null;
    log('sessions_revoked', a.person, { sessions: 1 });
    return clone(a);
  },
  async anonymisePerson(id: string) {
    await wait(150);
    const a = must(access.get(id), 'Человек');
    a.person.name = 'Бывший сотрудник клиента';
    a.person.initials = 'БС';
    a.email = `anonymised-${id}@anonymised.invalid`;
    a.disabled = true;
    a.lastSeen = null;
    log('person_anonymised', a.person, { sessions: 0 });
    return clone(a);
  },
  async personSessions(id: string) {
    await wait();
    const a = must(access.get(id), 'Человек');
    return a.lastSeen && !a.disabled ? [{ createdAt: a.lastSeen, lastSeenAt: a.lastSeen, expiresAt: new Date(new Date(a.lastSeen).getTime() + 30 * 86_400_000).toISOString() }] : [];
  },
  async people() {
    await wait();
    for (const c of clients)
      for (const x of c.contacts)
        if (!access.has(x.person.id))
          access.set(x.person.id, { person: x.person, email: x.email, side: 'client', studioRole: null, clientRole: x.role, clientId: c.id, clientName: c.name, invited: x.invited, lastSeen: null, disabled: false });
    return clone([...access.values()]);
  },
  async audit(opts?: AuditFilter & { limit?: number; before?: number }) {
    await wait();
    const limit = opts?.limit ?? 50;
    const rows = filterAudit(opts).filter((e) => opts?.before === undefined || e.id < opts.before).slice(0, limit);
    return clone({ items: rows, next: rows.length === limit ? rows[rows.length - 1].id : null });
  },
  async auditCsv(filter?: AuditFilter) {
    await wait();
    const head = ['id', 'at', 'action', 'actor', 'client', 'data'];
    const body = filterAudit(filter).map((e) => [e.id, e.at, e.action, e.actor?.name ?? '', e.clientName ?? '', JSON.stringify(e.data)]);
    return new Blob([[head, ...body].map((r) => r.map((x) => JSON.stringify(String(x))).join(',')).join('\n')], { type: 'text/csv' });
  },
  async clients() {
    await wait();
    const last = lastByThread();
    return clone(
      clients.map((c): ClientSummary => {
        const ps = projects.filter((p) => p.clientId === c.id);
        const ids = new Set(ps.map((p) => p.id));
        const ths = threads.filter((t) => ids.has(t.projectId));
        return {
          ...c,
          projects: ps.length,
          mrr: { amountMinor: ps.filter((p) => p.kind === 'retainer').reduce((n, p) => n + p.terms.price.amountMinor, 0), currency: 'RUB' },
          receivable: {
            amountMinor: invoices.filter((i) => ids.has(i.projectId ?? '') && (i.status === 'sent' || i.status === 'overdue')).reduce((n, i) => n + i.total.amountMinor, 0),
            currency: 'RUB',
          },
          overdue: {
            amountMinor: invoices.filter((i) => ids.has(i.projectId ?? '') && i.status === 'overdue').reduce((n, i) => n + i.total.amountMinor, 0),
            currency: 'RUB',
          },
          waitingOnUs: ths.filter((t) => needsAnswer(last.get(t.id))).length,
          waitingOnClient: tasks.filter((t) => ids.has(t.projectId) && t.needsClient && t.status !== 'done').length,
          lastContactAt: ths[0]?.lastMessageAt,
        };
      }),
    );
  },
  async client(id) {
    await wait();
    return clone({ ...must(clients.find((c) => c.id === id), 'Клиент'), payers: payerSummaries(id) });
  },
  async addClientPayer(clientId, { legal, makeDefault }) {
    await wait(200);
    if (!legal.legalName?.trim()) throw new Error('Укажите название плательщика');
    checkRuLegal(legal, true);
    const id = `pay-${mockPayers.length + 1}`;
    payersOf(clientId);
    mockPayers.push({ id, clientId, legal: { taxId: '', kpp: '', ogrn: '', address: '', ...legal }, email: '', isDefault: false });
    if (makeDefault) makeDefaultPayer(clientId, id);
    return payerSummaries(clientId);
  },
  async updateClientPayer(clientId, payerId, { legal, makeDefault }) {
    await wait(200);
    const p = mustPayer(clientId, payerId);
    const next = { ...p.legal, ...legal };
    checkRuLegal(next, true);
    p.legal = next;
    if (makeDefault) makeDefaultPayer(clientId, payerId);
    mirrorDefault(clientId);
    return payerSummaries(clientId);
  },
  async setClientDefaultPayer(clientId, payerId) {
    await wait(150);
    makeDefaultPayer(clientId, payerId);
    return payerSummaries(clientId);
  },
  async createClient(input) {
    await wait(250);
    const id = input.name.toLowerCase().replace(/[^a-zа-я0-9]+/gi, '-').replace(/^-|-$/g, '') || `c${clients.length + 1}`;
    const c: Client = {
      id: clients.some((x) => x.id === id) ? `${id}-${clients.length}` : id,
      name: input.name,
      legalName: input.legal?.legalName ?? input.legalName ?? '',
      taxId: input.legal?.taxId ?? input.taxId ?? '',
      legal: { legalName: input.legal?.legalName ?? input.legalName ?? '', taxId: input.legal?.taxId ?? input.taxId ?? '', kpp: '', ogrn: '', address: '', ...input.legal },
      contour: input.contour,
      stage: 'active',
      since: day(0),
      manager: people.manager,
      contacts: input.contacts.map((x, i) => ({ person: clientPerson(`u-${Date.now()}-${i}`, x.name, x.role === 'accountant' ? 'Бухгалтерия' : 'Представитель клиента'), email: x.email, role: x.role, invited: 'pending' })),
    };
    clients.unshift(c);
    return clone(c);
  },
  async updateClient(id, patch) {
    await wait(200);
    const c = must(clients.find((x) => x.id === id), 'Клиент');
    if (patch.name?.trim()) c.name = patch.name.trim();
    if (patch.stage) c.stage = patch.stage;
    if (patch.manager_id) c.manager = must(studioTeam.find((p) => p.id === patch.manager_id), 'Менеджер');
    if (patch.actsEnabled !== undefined) c.actsEnabled = patch.actsEnabled;
    if (patch.apiEnabled !== undefined) c.apiEnabled = patch.apiEnabled;
    if (patch.legal) {
      const legal = { ...c.legal, ...patch.legal };
      checkRuLegal(legal, true);
      Object.assign(c, { legal, legalName: legal.legalName ?? '', taxId: legal.taxId ?? '' });
      payersOf(id)[0].legal = { ...legal };
    }
    return clone({ ...c, payers: payerSummaries(id) });
  },
  async contour() {
    await wait();
    return clone(workspace.contour);
  },
  async addContact(clientId, input) {
    await wait(200);
    const c = must(clients.find((x) => x.id === clientId), 'Клиент');
    const email = input.email.trim().toLowerCase();
    if (!input.name.trim() || !email.includes('@')) throw new Error('Укажите имя и адрес почты контакта');
    if (clients.some((x) => x.contacts.some((k) => k.email.toLowerCase() === email))) throw new Error('Не удалось добавить контакт с этим адресом. Проверьте адрес или напишите в поддержку.');
    c.contacts.push({ person: clientPerson(`u-${Date.now()}`, input.name.trim(), input.role === 'accountant' ? 'Бухгалтерия' : 'Представитель клиента'), email, role: input.role, invited: 'pending' });
    return { ...clone(c), invited: true };
  },
  async inviteContact(clientId, email) {
    await wait(200);
    const c = must(clients.find((x) => x.id === clientId), 'Клиент');
    const contact = must(c.contacts.find((x) => x.email === email), 'Контакт');
    contact.invited = 'pending';
    return clone(c);
  },

  async projects(clientId) {
    await wait();
    return clone(clientId ? projects.filter((p) => p.clientId === clientId) : projects);
  },
  async project(id) {
    await wait();
    return clone(must(projects.find((p) => p.id === id), 'Проект'));
  },
  async createProject(input) {
    await wait(300);
    const owner = must(studioTeam.find((p) => p.id === input.ownerId), 'Руководитель');
    const key = input.title.replace(/[^A-Za-zА-Яа-я]/g, '').slice(0, 4).toUpperCase() || 'PRJ';
    const id = `${key.toLowerCase()}-${projects.length + 1}`;
    const base = {
      id,
      clientId: input.clientId,
      key,
      title: input.title,
      summary: input.summary,
      health: 'on_track' as const,
      now: input.now,
      owner,
      team: studioTeam.filter((p) => input.teamIds.includes(p.id) || p.id === owner.id),
      updatedAt: nowIso(),
      linear: input.linearProject ? { name: input.linearProject, url: 'https://linear.app/rodion-ch' } : undefined,
    };
    const p: Project =
      input.kind === 'fixed'
        ? {
            ...base,
            kind: 'fixed',
            finishBy: input.finishBy ?? day(30),
            milestones: (input.milestones?.length ? input.milestones : [{ title: 'Старт' }]).map((m, i) => ({ id: `m${i}`, title: m.title, due: m.due, state: i === 0 ? 'current' : 'next' })),
            terms: { price: input.price, since: day(0) },
          }
        : {
            ...base,
            kind: 'retainer',
            period: 'эта неделя',
            rhythm: (input.rhythm ?? []).map((r, i) => ({ id: `r${i}`, title: r.title, cadence: everyLabel[r.every], every: r.every, source: r.source, label: r.label })),
            metrics: [],
            terms: { price: input.price, since: day(0), period: 'month', nextInvoiceAt: day(30) },
          };
    projects.push(p);
    post(`t-${id}`, owner, `Проект «${p.title}» запущен. Здесь общая переписка по нему, задачи появятся во вкладке «Задачи».`, nowIso());
    rebuildThreads();
    return clone(p);
  },
  async updateNow(projectId, now, h) {
    await wait(200);
    const p = must(projects.find((x) => x.id === projectId), 'Проект');
    p.now = now;
    p.health = h;
    p.updatedAt = nowIso();
    return clone(p);
  },
  async updateProject(id, patch) {
    await wait(200);
    const p = must(projects.find((x) => x.id === id), 'Проект');
    if (patch.title !== undefined) {
      if (!patch.title.trim()) throw new Error('Название — от 1 до 300 знаков');
      p.title = patch.title.trim();
    }
    if (patch.summary !== undefined) p.summary = patch.summary;
    if (patch.price) p.terms.price = patch.price;
    if (patch.until !== undefined) p.terms.until = patch.until || undefined;
    if (patch.billing && p.kind === 'retainer') {
      const { day, covers, nextPeriod } = patch.billing;
      if (day < 1 || day > 28) throw new Error('День выставления счёта — с 1 по 28');
      const last = p.terms.lastInvoicedPeriod;
      if (last && nextPeriod <= last) throw new Error(`Счёт за ${periodLabel(last)} уже выставлен`);
      Object.assign(p.terms, { billingDay: day, billingCovers: covers, nextPeriod, nextInvoiceAt: invoiceDate(nextPeriod, covers, day) });
    }
    if (patch.payerId !== undefined) p.payerId = patch.payerId ? mustPayer(p.clientId, patch.payerId).id : null;
    if (patch.status) p.health = patch.status === 'active' ? (p.health === 'paused' || p.health === 'done' ? 'on_track' : p.health) : patch.status;
    if (patch.ownerId) p.owner = must(studioTeam.find((x) => x.id === patch.ownerId), 'Ответственный');
    if (patch.teamIds || patch.ownerId) {
      const ids = new Set([p.owner.id, ...(patch.teamIds ?? p.team.map((x) => x.id))]);
      p.team = studioTeam.filter((x) => ids.has(x.id));
    }
    if (patch.rhythm && p.kind === 'retainer') {
      const old = new Map(p.rhythm.map((r) => [r.id, r]));
      p.rhythm = patch.rhythm.map((r, i) => {
        const was = r.id ? old.get(r.id) : undefined;
        return { ...(r.source === was?.source ? was : {}), id: r.id ?? `r${i}`, title: r.title, cadence: everyLabel[r.every], every: r.every, source: r.source, label: r.label };
      });
    }
    if (p.kind === 'fixed') {
      if (patch.finishBy !== undefined) p.finishBy = patch.finishBy || p.finishBy;
      if (patch.milestones) p.milestones = patch.milestones.map((m, i) => ({ id: m.id ?? `m${i}`, title: m.title, due: m.due ?? undefined, state: m.state }));
    }
    p.updatedAt = nowIso();
    return clone(p);
  },
  async deleteProject(id) {
    await wait(200);
    const i = projects.findIndex((x) => x.id === id);
    if (i < 0) throw new Error('Проект не найден');
    const p = projects[i];
    if (invoices.some((x) => x.projectId === id)) throw new Error('Проект нельзя удалить: выставлены счета. Завершите проект (статус в настройках) — удалить можно только проект без счетов и документов.');
    projects.splice(i, 1);
    const gone = new Set([`t-${id}`, ...tasks.filter((t) => t.projectId === id).map((t) => t.threadId)]);
    for (let j = messages.length - 1; j >= 0; j--) if (gone.has(messages[j].threadId)) messages.splice(j, 1);
    for (const list of [tasks, documents]) {
      for (let j = list.length - 1; j >= 0; j--) if ((list[j] as { projectId?: string }).projectId === id) list.splice(j, 1);
    }
    rebuildThreads();
    return { ok: true as const, clientId: p.clientId };
  },
  async linearProjects() {
    return {
      connected: true,
      projects: [
        { id: 'lp-1', name: 'Карандаш — новый сайт', url: 'https://linear.app/rodion-ch/project/site' },
        { id: 'lp-2', name: 'Карандаш — лендинг домов', url: 'https://linear.app/rodion-ch/project/land' },
        { id: 'lp-3', name: 'Студия — внутренние задачи', url: 'https://linear.app/rodion-ch/project/inner' },
      ],
    };
  },
  async linkLinear(projectId, name) {
    await wait(200);
    const p = must(projects.find((x) => x.id === projectId), 'Проект');
    if (p.linear && p.linear.name.toLowerCase() === name.trim().toLowerCase()) throw new Error('Проект уже связан с этим проектом Linear');
    p.linear = { name: name.trim(), url: 'https://linear.app/rodion-ch' };
    return clone(p);
  },
  async rhythmDone(projectId, rhythmId) {
    await wait(150);
    const p = must(projects.find((x) => x.id === projectId), 'Проект');
    if (p.kind !== 'retainer') throw new Error('Проект без регулярной работы');
    const r = must(p.rhythm.find((x) => x.id === rhythmId), 'Работа');
    if (r.source && r.source !== 'manual') throw new Error('Эта работа считается по отчётам, встречам или Linear: вручную не отмечается');
    const today = new Date();
    const next = new Date(today);
    if (r.every === 'month') next.setMonth(next.getMonth() + 1);
    else next.setDate(next.getDate() + (r.every === '2weeks' ? 14 : 7));
    r.lastDone = day(0);
    r.next = r.every ? next.toISOString().slice(0, 10) : null;
    r.overdue = false;
    p.updatedAt = nowIso();
    return clone(p);
  },
  async unlinkLinear(projectId) {
    await wait(150);
    const p = must(projects.find((x) => x.id === projectId), 'Проект');
    if (!p.linear) throw new Error('Проект не связан с Linear');
    p.linear = undefined;
    return clone(p);
  },
  async setTasksVisible(projectId, ids, visible) {
    await wait(150);
    for (const id of ids) must(tasks.find((x) => x.id === id && x.projectId === projectId), 'Задача').clientVisible = visible;
    for (const t of tasks) if (ids.includes(t.id)) t.updatedAt = nowIso();
    return clone(tasks.filter((t) => t.projectId === projectId));
  },
  async documents(opts) {
    await wait();
    const owner = (d: DocumentRef) => projects.find((p) => p.id === d.projectId)?.clientId ?? CLIENT_ID;
    return clone(documents.filter((d) => (!opts?.clientId || owner(d) === opts.clientId) && (!opts?.projectId || d.projectId === opts.projectId)).map((d) => ({ ...d, clientId: owner(d) })));
  },
  async uploadDocument({ clientId, projectId, file }) {
    await wait(300);
    const d: DocumentRef = { id: `d${documents.length + 100}`, name: file.name, kind: 'other', projectId, uploadedBy: people.manager, uploadedAt: nowIso(), sizeKb: Math.max(1, Math.round(file.size / 1024)) };
    documents.unshift(d);
    return { ...clone(d), clientId };
  },
  async downloadReportPdf() {
    await wait();
    return new Blob(['%PDF-1.4 demo report'], { type: 'application/pdf' });
  },
  async downloadDocument() {
    await wait();
    return new Blob(['demo'], { type: 'application/octet-stream' });
  },

  async tasks(projectId, opts) {
    await wait();
    return clone(tasks.filter((t) => t.projectId === projectId && (!opts?.visible || t.clientVisible)));
  },
  async createTask(input) {
    await wait(300);
    addTask({
      projectId: input.projectId,
      group: input.group,
      title: input.title,
      description: input.description,
      status: 'todo',
      assignee: must(studioTeam.find((p) => p.id === input.assigneeId), 'Исполнитель'),
      internal: !input.clientVisible,
      age: 0,
    });
    const t = tasks.at(-1)!;
    t.due = input.due;
    return clone(t);
  },
  async updateTask(id, patch) {
    await wait(150);
    const t = must(tasks.find((x) => x.id === id), 'Задача');
    if (patch.clientVisible !== undefined) t.clientVisible = patch.clientVisible;
    if (patch.status) t.status = patch.status;
    if (patch.due !== undefined) t.due = patch.due;
    if (patch.needsClient !== undefined) t.needsClient = patch.needsClient ?? undefined;
    t.updatedAt = nowIso();
    const p = projects.find((x) => x.id === t.projectId)!;
    p.health = tasks.some((x) => x.projectId === p.id && x.needsClient && x.status !== 'done' && x.clientVisible) ? 'waiting_client' : p.health === 'waiting_client' ? 'on_track' : p.health;
    return clone(t);
  },

  async threads(f?: StudioThreadFilter) {
    await wait();
    const last = lastByThread();
    const needle = f?.q?.trim().toLowerCase();
    const hit = (t: Thread) => !needle || t.title.toLowerCase().includes(needle) || messages.some((m) => m.threadId === t.id && m.body.toLowerCase().includes(needle));
    const list = threads
      .map((t) => withWaiting(t, last.get(t.id)))
      .filter((t) => (!f?.clientId || t.clientId === f.clientId) && (!f?.projectId || t.projectId === f.projectId) && (!f?.waiting || t.waitingOnUs) && hit(t));
    // The waiting ones first, the longest waiting on top; then the rest, newest first.
    const rest = (t: StudioThread) => (t.waitingOnUs ? 0 : 1);
    return clone(list.sort((a, b) => rest(a) - rest(b) || (a.waitingOnUs ? a.lastMessageAt.localeCompare(b.lastMessageAt) : b.lastMessageAt.localeCompare(a.lastMessageAt))));
  },
  async createThread(input) {
    await wait(120);
    must(projects.find((p) => p.id === input.projectId), 'Проект');
    if (input.taskId) must(tasks.find((t) => t.id === input.taskId && t.projectId === input.projectId), 'Задача');
    return openDiscussion(input, studioTeam[0]);
  },
  async closeThread(threadId) {
    await wait(120);
    must(threads.find((t) => t.id === threadId), 'Переписка');
    closedAt[threadId] = nowIso();
  },
  async reply(threadId, body, authorId) {
    await wait(120);
    post(threadId, must(studioTeam.find((p) => p.id === authorId), 'Автор'), body, nowIso());
    rebuildThreads();
    return clone(messages.at(-1)!);
  },

  async catalog() {
    return clone(catalog);
  },
  async invoices(clientId) {
    await wait();
    const ids = new Set(projects.filter((p) => !clientId || p.clientId === clientId).map((p) => p.id));
    const clientOf = (i: Invoice) => projects.find((x) => x.id === i.projectId)?.clientId ?? draftClient.get(i.id) ?? null;
    return clone(
      invoices
        .filter((i) => !clientId || ids.has(i.projectId ?? '') || draftClient.get(i.id) === clientId)
        .map((i) => {
          const c = clients.find((x) => x.id === clientOf(i));
          return { ...i, clientId: c?.id ?? null, clientName: c?.name ?? null, remindedAt: reminders.get(i.id) ?? null, inUrl: null };
        }),
    );
  },
  async issueInvoice(input) {
    await wait(300);
    const lines = invoiceLines(input);
    const total = lines.reduce((n, l) => n + l.total.amountMinor, 0);
    const inv: Invoice = {
      id: `i${invoices.length + 1}`,
      number: String(invoices.length + 1).padStart(4, '0'),
      issuedAt: day(0),
      dueAt: input.dueDate ?? day(input.dueDays ?? 5),
      status: input.draft ? 'draft' : 'sent',
      total: { amountMinor: total, currency: 'RUB' },
      paid: rub(0),
      lines,
      projectId: input.projectId,
      basis: input.basis || null,
      comment: input.comment || null,
    };
    // The payer picked, else the project's, else the client's default.
    const proj = projects.find((p) => p.id === input.projectId && p.clientId === input.clientId);
    inv.payer = invoicePayer(input.payerId ?? (proj ? projectPayerId(proj) : payersOf(input.clientId)[0].id), input.clientId);
    invoices.unshift(inv);
    draftClient.set(inv.id, input.clientId);
    return clone(inv);
  },
  async updateDraftInvoice(id, input) {
    await wait(200);
    const inv = must(invoices.find((x) => x.id === id), 'Счёт');
    if (inv.status !== 'draft') throw new Error('Счёт уже выставлен: изменить можно только черновик');
    const lines = invoiceLines(input);
    Object.assign(inv, { lines, total: { amountMinor: lines.reduce((n, l) => n + l.total.amountMinor, 0), currency: 'RUB' }, projectId: input.projectId, basis: input.basis || null, comment: input.comment || null, dueAt: input.dueDate ?? day(input.dueDays ?? 5) });
    return clone(inv);
  },
  async deleteDraftInvoice(id) {
    await wait(150);
    const inv = must(invoices.find((x) => x.id === id), 'Счёт');
    if (inv.status !== 'draft') throw new Error('Счёт уже выставлен: изменить можно только черновик');
    invoices.splice(invoices.indexOf(inv), 1);
  },
  async sendInvoice(id) {
    await wait(200);
    const inv = must(invoices.find((x) => x.id === id), 'Счёт');
    if (inv.status !== 'draft') throw new Error('Счёт уже выставлен: изменить можно только черновик');
    inv.status = 'sent';
    notify(`Новый счёт № ${inv.number}`, `/money/${inv.id}`);
    return clone(inv);
  },
} satisfies Partial<StudioApi>;

// ---------------------------------------------------------------------------
// Full client cycle

const proposals: Proposal[] = [
  {
    id: 'kp1',
    clientId: 'karandash',
    number: 'КП-004',
    title: 'Раздел «Проекты домов» с каталогом и калькулятором',
    intro: 'Каталог из 12 проектов домов с планировками и ценой под ключ, калькулятор комплектации и заявка с выбранным проектом прямо в amoCRM. Нужен под растущий спрос на строительство.',
    kind: 'fixed',
    lines: [{ title: 'Раздел «Проекты домов»', note: 'Дизайн, вёрстка, калькулятор, интеграция с amoCRM', quantity: 1, price: rub(120000), total: rub(120000) }],
    total: rub(120000),
    milestones: [
      { title: 'Структура и калькулятор', weeks: 1 },
      { title: 'Дизайн', weeks: 2 },
      { title: 'Вёрстка и запуск', weeks: 2 },
    ],
    prepaymentPercent: 50,
    validUntil: day(12),
    status: 'sent',
    sentAt: hoursAgo(20),
  },
  {
    id: 'kp2',
    clientId: 'apart',
    number: 'КП-003',
    title: 'Замер времени ответа на заявки',
    intro: 'Две недели замеряем, как быстро отвечают на заявки с сайта и Авито, и даём список правок.',
    kind: 'fixed',
    lines: [{ title: 'Аудит скорости ответа', note: 'Разовая работа', quantity: 1, price: rub(12000), total: rub(12000) }],
    total: rub(12000),
    milestones: [{ title: 'Замер и отчёт', weeks: 2 }],
    prepaymentPercent: 100,
    validUntil: day(-15),
    status: 'sent',
    sentAt: hoursAgo(29 * 24),
  },
];

const signRequests: SignRequest[] = [
  { id: 'sg1', kind: 'contract', title: 'Договор № 12-2026', documentName: 'Договор № 12-2026.pdf', documentId: 'd1', createdAt: hoursAgo(28 * 24), status: 'signed', signedAt: hoursAgo(27 * 24), signedBy: 'Алексей Громов', hash: 'sha256:9f2c…a41e' },
  { id: 'sg2', kind: 'addendum', title: 'Допсоглашение № 1: лендинг «Загородные дома»', documentName: 'Допсоглашение № 1.pdf', documentId: 'd1', projectId: 'land', createdAt: hoursAgo(30), status: 'to_sign' },
];

const onboarding: OnboardingItem[] = [
  { id: 'ob2', projectId: 'land', kind: 'contact', title: 'Кто отвечает на заявки по домам', hint: 'Имя и телефон, чтобы форма вела к нужному человеку', done: true, doneAt: hoursAgo(100) },
  { id: 'ob3', projectId: 'land', kind: 'access', title: 'Доступ к хостингу лендинга', hint: 'Передайте через «Доступы»', done: true, doneAt: hoursAgo(110) },
  { id: 'ob4', projectId: 'land', kind: 'meeting', title: 'Созвон по офферу', hint: '30 минут: цены, сроки, что обещаем', done: true, doneAt: hoursAgo(130) },
  { id: 'ob5', projectId: 'ads', kind: 'access', title: 'Гостевой доступ к Директу и Метрике', hint: 'Через «Доступы», роль — представитель', done: true, doneAt: hoursAgo(27 * 24) },
  { id: 'ob8', projectId: 'site', kind: 'materials', title: 'Логотип и фирменные цвета в векторе', hint: 'SVG или PDF, если есть брендбук — его тоже', done: false },
  { id: 'ob9', projectId: 'site', kind: 'access', title: 'Доступ к Яндекс Вебмастеру', hint: 'Чтобы перенести сайт без потери позиций', done: false },
  { id: 'ob10', projectId: 'site', kind: 'contact', title: 'Кто согласует тексты', hint: 'Если не вы — имя и почта', done: true, doneAt: hoursAgo(20 * 24) },
  { id: 'ob11', projectId: 'site', kind: 'meeting', title: 'Стартовый созвон', hint: 'Цели, сроки, кто принимает решения', done: true, doneAt: hoursAgo(26 * 24) },
  { id: 'ob7', projectId: 'ads', kind: 'contact', title: 'Кто принимает звонки с рекламы', hint: 'Для проверки качества заявок', done: true, doneAt: hoursAgo(26 * 24) },
];

const changes: ChangeRequest[] = [
  {
    id: 'cr1', projectId: 'site', title: 'Раздел отзывов с видео', description: 'Хотим 3–4 видеоотзыва от клиентов на главной и отдельной странице.', author: people.me, createdAt: hoursAgo(50), status: 'estimated',
    estimate: { price: rub(18000), days: 5, note: 'Плеер, страница отзывов, блок на главной. Съёмка — ваша.' },
  },
  { id: 'cr2', projectId: 'site', title: 'Поменять цвет кнопок на зелёный', description: 'Как в фирменном стиле.', author: people.me, createdAt: hoursAgo(120), status: 'in_scope', decidedAt: hoursAgo(110) },
  {
    id: 'cr3', projectId: 'site', title: 'Калькулятор стоимости ремонта', goal: 'Чтобы клиент сам прикинул бюджет и оставлял заявку теплее', description: 'Как у конкурентов: площадь, тип ремонта, материалы.', urgency: 'month', author: people.me, createdAt: hoursAgo(30), status: 'question', question: 'Цены за м² берём из вашего прайса или нужны диапазоны?',
    history: [
      { at: hoursAgo(30), kind: 'created', by: people.me },
      { at: hoursAgo(6), kind: 'asked', by: people.manager, text: 'Цены за м² берём из вашего прайса или нужны диапазоны?' },
    ],
  },
  {
    id: 'cr4', projectId: 'site', title: 'Форма заявки на замер', description: 'Пришло из amoCRM: клиенты просят записаться на замер с сайта.', author: { id: 'u-api-amo', name: 'Интеграция «amoCRM»', role: '', side: 'client', initials: 'И', kind: 'api' }, createdAt: hoursAgo(20), status: 'new',
    externalId: 'AMO-1042', externalUrl: 'https://karandash.amocrm.example/leads/detail/1042',
  },
];
const forClient = (c: ChangeRequest): ChangeRequest => {
  const out = clone(c);
  delete out.taskUrl;
  // The client follows the task only once the studio took the initiative.
  if (!['in_scope', 'accepted', 'done'].includes(out.status)) delete out.task;
  return out;
};
const groupOf = (projectId: string) => {
  const p = projects.find((x) => x.id === projectId)!;
  return p.kind === 'fixed' ? (p.milestones.find((m) => m.state !== 'done')?.title ?? 'Доп. работы') : 'Кампании';
};
/** On submit the brief goes to the studio's Linear inbox at once (client_inbox): a task the client does not see yet. */
function fileChange(c: ChangeRequest) {
  const p = projects.find((x) => x.id === c.projectId)!;
  const key = addTask({ projectId: c.projectId, group: groupOf(c.projectId), title: c.title, description: [c.goal, c.description].filter(Boolean).join('\n\n'), status: 'backlog', assignee: p.owner, age: 0, internal: true });
  c.task = { key, id: key.toLowerCase() };
  c.taskUrl = tasks.find((t) => t.key === key)?.linearUrl;
}
/** Taken into work: off the inbox, shown to the client (a request from before the inbox gets its task now). */
function taskOfChange(c: ChangeRequest, history: ChangeEvent[]) {
  if (c.task) {
    const t = tasks.find((x) => x.key === c.task!.key);
    if (t) t.clientVisible = true;
    return;
  }
  const p = projects.find((x) => x.id === c.projectId)!;
  const key = addTask({ projectId: c.projectId, group: p.kind === 'fixed' ? (p.milestones.find((m) => m.state !== 'done')?.title ?? 'Доп. работы') : 'Кампании', title: c.title, description: [c.goal, c.description].filter(Boolean).join('\n\n'), status: 'backlog', assignee: p.owner, age: 0 });
  c.task = { key, id: key.toLowerCase() };
  c.taskUrl = tasks.find((t) => t.key === key)?.linearUrl;
  pushChange(c, history, { at: nowIso(), kind: 'task_created', by: null, key });
}
const OPEN: ChangeRequest['status'][] = ['new', 'question', 'parked'];

/** The story of a request: the stored one, or rebuilt from the fields of one made before it was kept. */
function changeHistory(c: ChangeRequest): ChangeEvent[] {
  if (c.history) return c.history;
  const out: ChangeEvent[] = [{ at: c.createdAt, kind: 'created', by: c.author }];
  if (c.estimate) out.push({ at: c.createdAt, kind: 'estimated', by: people.manager, price: c.estimate.price, days: c.estimate.days, note: c.estimate.note });
  if (c.status === 'in_scope') out.push({ at: c.decidedAt ?? c.createdAt, kind: 'in_scope', by: people.manager });
  if (c.status === 'declined') out.push({ at: c.decidedAt ?? c.createdAt, kind: 'declined', by: c.declinedBy ? people.manager : c.author, reason: c.declineReason, side: c.declinedBy ? 'studio' : 'client' });
  if (c.status === 'accepted') out.push({ at: c.decidedAt ?? c.createdAt, kind: 'accepted', by: c.author });
  return out;
}
function pushChange(c: ChangeRequest, before: ChangeEvent[], ...events: ChangeEvent[]) {
  c.history = [...(c.history ?? before), ...events];
}
let lastInvoiceN = 0;
/** A new invoice for the request's estimate, in the demo's list. */
function invoiceOfChange(c: ChangeRequest): Invoice {
  const price = c.estimate!.price;
  // A canceled invoice leaves the list: the counter only goes up, so a new one never reuses its number.
  const n = Math.max(invoices.length + 1, lastInvoiceN + 1);
  lastInvoiceN = n;
  const inv: Invoice = {
    id: `i${n}`, number: String(n).padStart(4, '0'), issuedAt: day(0), dueAt: day(5), status: 'sent',
    total: price, paid: rub(0), projectId: c.projectId,
    lines: [{ title: c.title, note: 'Дополнительная работа по запросу', quantity: 1, price, total: price }],
  };
  invoices.unshift(inv);
  return inv;
}
/** The request with its story and the invoice's state; an invoice no longer in the list was canceled. */
function changeDetail(c: ChangeRequest): ChangeDetail {
  const out: ChangeDetail = { ...clone(c), history: clone(changeHistory(c)) };
  if (c.status === 'accepted' && c.invoiceId) {
    const inv = invoices.find((i) => i.id === c.invoiceId);
    out.invoice = inv ? { id: inv.id, number: inv.number, status: inv.status, dead: false, total: inv.total, dueAt: inv.dueAt } : { id: c.invoiceId, number: cancelledInvoices.find((x) => x.id === c.invoiceId)?.number ?? null, status: 'cancelled', dead: true };
    out.canReinvoice = out.invoice.dead;
  }
  return out;
}

const reports: Report[] = [
  {
    id: 'rp1', projectId: 'ads', period: 'Прошлая неделя', publishedAt: hoursAgo(33), author: people.ads,
    summary: 'Заявок больше, каждая дешевле: после чистки запросов реклама перестала тратить деньги на «ремонт своими руками» и вакансии.',
    numbers: [
      { label: 'Заявки', value: '6', change: '+2' },
      { label: 'Стоимость заявки', value: '4 180 ₽', change: '−18 %' },
      { label: 'Расход', value: '25 100 ₽', change: 'в плане' },
      { label: 'Доля мусора', value: '12 %', change: '−9 п.' },
    ],
    done: ['Добавили 44 минус-слова', 'Автотаргетинг ЕПК оставили только на бренде', 'Проверили подмену номера на всех страницах'],
    next: ['Запуск кампании на загородные дома', 'Тест оффера «Смета по плану»'],
    fileName: 'Отчёт по рекламе за прошлую неделю.pdf',
  },
  {
    id: 'rp2', projectId: 'ads', period: 'Позапрошлая неделя', publishedAt: hoursAgo(8 * 24 + 9), author: people.ads,
    summary: 'Нашли, куда уходили звонки: подмена номера работала не на всех страницах. Поправили.',
    numbers: [
      { label: 'Заявки', value: '4', change: '0' },
      { label: 'Стоимость заявки', value: '5 100 ₽', change: '−9 %' },
      { label: 'Расход', value: '24 800 ₽', change: 'в плане' },
    ],
    done: ['Сверили звонки с amoCRM', 'Остановили РСЯ без заявок'],
    next: ['Минус-слова', 'Проверка подмены номера'],
  },
];

const meetings: Meeting[] = [
  { id: 'mt1', projectId: 'ads', at: hoursAgo(-3 * 24 + 6), title: 'Созвон по результатам рекламы', durationMin: 45, status: 'planned', agenda: ['Итоги двух недель', 'Бюджет на кампанию по домам', 'Что делать с Мастером кампаний'], link: 'https://telemost.yandex.ru/demo' },
  { id: 'mt2', projectId: 'site', at: hoursAgo(-2 * 24 + 4), title: 'Показ прототипов', agenda: ['Главная v2', 'Страница услуги', 'Цены'] },
  {
    id: 'mt3', projectId: 'site', at: hoursAgo(5 * 24), title: 'Созвон по структуре', agenda: ['Карта страниц', 'Квиз'],
    outcome: { summary: 'Структуру утвердили, раздел цен делаем отдельной страницей.', decisions: ['Квиз — на первом экране', 'Раздел «Цены» — отдельная страница', 'Отзывы берём из Яндекса'] },
  },
];

const notifications: Notification[] = [
  { id: 'n1', at: hoursAgo(2.5), text: 'Мария Орлова выложила прототип главной v2 — ждёт утверждения', to: '/p/site/tasks/site-7', read: false },
  { id: 'n2', at: hoursAgo(4), text: 'Родион передал вам доступ к стейджингу', to: '/access', read: false },
  { id: 'n3', at: hoursAgo(9), text: 'Павел Ким спрашивает про бюджет на кампанию по домам', to: '/p/ads/talk/t-ads', read: false },
  { id: 'n4', at: hoursAgo(20), text: 'Новое предложение: раздел «Проекты домов»', to: '/proposals/kp1', read: true },
  { id: 'n5', at: hoursAgo(33), text: 'Опубликован отчёт по рекламе за прошлую неделю', to: '/p/ads/reports', read: true },
];

// The demo starts «seen» so it does not cover every page; «Как пользоваться кабинетом» opens it.
let prefs: NotificationPrefs = { email: true, telegram: false, digest: 'daily', tourSeenAt: '2026-01-01T00:00:00Z' };
let payer: PayerDetails = { legalName: 'ООО «Карандаш» (демо)', taxId: '7800000000', kpp: '780001001', ogrn: '', address: 'г. Санкт-Петербург, демо-адрес', email: 'buh@karandash.example' };
const feedbacks: Feedback[] = [{ id: 'fb1', projectId: 'ads', score: 9, comment: 'Отчёты понятные, видно, за что платим.', at: hoursAgo(9 * 24), context: 'Первый месяц рекламы' }];
const checkoutProblems: CheckoutProblem[] = [
  { id: 'co1', clientId: CLIENT_ID, invoiceId: 'i3', invoiceNumber: '0003', provider: 'tochka', operationId: 'op-demo-1', status: 'mismatch', problem: 'amount_mismatch', amount: rub(40000), bankAmount: rub(4000), overpaid: null, createdAt: hoursAgo(5), problemAt: hoursAgo(4), settledAt: null, resolvedAt: null, resolvedNote: null },
];
let undelivered: UndeliveredNotification[] = [
  { id: 'un1', at: hoursAgo(3), text: 'Клиент «Карандаш» написал в проекте «Реклама»', audience: 'studio', clientName: 'Карандаш', recipients: [{ person: { id: 'u-own', name: 'Анна Соколова' }, tries: 5 }] },
];
const statement: StatementLine[] = [
  { id: 'st1', date: day(-3), amount: rub(65000), payer: 'ООО «Карандаш»', purpose: 'Оплата по счёту № 0002 от …', status: 'matched', allocations: [{ invoiceId: 'i2', number: '0002', amountMinor: 6500000 }], allocated: rub(65000), suggestions: [] },
  { id: 'st2', date: day(0), amount: rub(40000), payer: 'ООО «Карандаш»', purpose: 'Оплата по сч 3 лендинг', status: 'unmatched', allocations: [], allocated: rub(0), suggestions: [] },
];
/** Suggestions in the demo: the exact amount, then the invoice number in the purpose, as the BFF does. */
const suggestFor = (l: StatementLine): StatementLine['suggestions'] =>
  l.status === 'unmatched' || l.status === 'partial'
    ? invoices
        .filter((i) => i.status === 'sent' || i.status === 'overdue')
        .flatMap((i) => {
          const basis = l.purpose.includes(i.number) ? ('number' as const) : i.total.amountMinor === l.amount.amountMinor ? ('amount' as const) : null;
          return basis ? [{ invoiceId: i.id, number: i.number, clientName: 'Карандаш', balance: i.total, basis }] : [];
        })
    : [];

/** When each invoice was last reminded; the demo has no clock for the 24h wait, so one reminder per session. */
const reminders = new Map<string, string>();
const karandash = () => clients.find((c) => c.id === CLIENT_ID)!;
/** This meeting, or it and the later live ones of its series. */
const seriesTargets = (m: Meeting, scope: MeetingScope) => (scope === 'following' && m.seriesId ? meetings.filter((x) => x.seriesId === m.seriesId && x.at >= m.at && !isCanceled(x)) : [m]);
/** A minimal calendar file for the demo: the real one comes from the BFF. */
function icsBlob(id: string, series: boolean) {
  const m = must(meetings.find((x) => x.id === id), 'Встреча');
  const list = series && m.seriesId ? meetings.filter((x) => x.seriesId === m.seriesId && !isCanceled(x)) : [m];
  const stamp = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, '');
  const events = list.map((x) => ['BEGIN:VEVENT', `UID:${x.id}@demo`, `DTSTAMP:${stamp(new Date())}`, `DTSTART:${stamp(new Date(x.at))}`, `DTEND:${stamp(meetingEnd(x))}`, `SUMMARY:${x.title}`, ...(x.link ? [`URL:${x.link}`] : []), isCanceled(x) ? 'STATUS:CANCELLED' : 'STATUS:CONFIRMED', 'END:VEVENT'].join('\r\n'));
  return new Blob([['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//mark-42//demo//RU', ...events, 'END:VCALENDAR', ''].join('\r\n')], { type: 'text/calendar' });
}
const notify = (text: string, to: string) => notifications.unshift({ id: `n${notifications.length + 1}`, at: nowIso(), text, to, read: false });
const fakeHash = (s: string) => `sha256:${[...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7).toString(16).padStart(8, '0')}…`;

const clientExtras = {
  async actions() {
    const base = await baseClient.actions();
    const mine = new Set(projects.filter((p) => p.clientId === CLIENT_ID).map((p) => p.id));
    const extra: ActionItem[] = [];
    proposals.filter((p) => p.clientId === CLIENT_ID && p.status === 'sent').forEach((proposal) => extra.push({ kind: 'proposal', proposal: clone(proposal) }));
    signRequests.filter((s) => s.status === 'to_sign').forEach((sign) => extra.push({ kind: 'sign', sign: clone(sign) }));
    changes.filter((c) => mine.has(c.projectId) && (c.status === 'estimated' || c.status === 'question')).forEach((change) => extra.push({ kind: 'change', change: forClient(change) }));
    // Access asked outside the launch comes here too: the launch block filters it out, «Ждём от вас» shows it.
    onboarding.filter((o) => mine.has(o.projectId) && !o.done).forEach((item) => extra.push({ kind: 'onboarding', item: clone(item) }));
    if (!feedbacks.some((f) => f.projectId === 'site')) extra.push({ kind: 'feedback', projectId: 'site', context: 'Этап «Бриф» завершён' });
    incompletePayers(CLIENT_ID).forEach((p) => extra.push({ kind: 'payer', payer: p, text: payerPrompt(p) }));
    return [...base, ...extra];
  },

  async proposals() {
    await wait();
    return clone(proposals.filter((p) => p.clientId === CLIENT_ID));
  },
  async proposal(id: string) {
    await wait();
    return clone(must(proposals.find((p) => p.id === id), 'Предложение'));
  },
  async acceptProposal(id: string) {
    await wait(300);
    const kp = must(proposals.find((p) => p.id === id), 'Предложение');
    if (kp.status !== 'sent') throw new Error('Предложение уже рассмотрено');
    const project = await baseStudio.createProject({
      clientId: kp.clientId,
      kind: kp.kind,
      title: kp.title,
      summary: kp.intro.split('.')[0],
      now: 'Предложение принято. Готовим старт: список того, что понадобится от вас, появится здесь.',
      price: kp.total,
      ownerId: 'u-pm',
      teamIds: ['u-des', 'u-dev'],
      milestones: kp.milestones.map((m) => ({ title: m.title })),
    });
    kp.status = 'accepted';
    kp.decidedAt = nowIso();
    kp.projectId = project.id;
    const pre = Math.round((kp.total.amountMinor * kp.prepaymentPercent) / 100);
    invoices.unshift({
      id: `i${invoices.length + 1}`, number: String(invoices.length + 1).padStart(4, '0'), issuedAt: day(0), dueAt: day(5), status: 'sent',
      total: { amountMinor: pre, currency: 'RUB' }, paid: rub(0), projectId: project.id,
      lines: [{ title: kp.title, note: `Предоплата ${kp.prepaymentPercent} % по ${kp.number}`, quantity: 1, price: { amountMinor: pre, currency: 'RUB' }, total: { amountMinor: pre, currency: 'RUB' } }],
    });
    onboarding.push(
      { id: `ob${onboarding.length + 1}`, projectId: project.id, kind: 'meeting', title: 'Стартовый созвон', hint: 'Цели, сроки, кто принимает решения', done: false },
      { id: `ob${onboarding.length + 2}`, projectId: project.id, kind: 'materials', title: 'Материалы для каталога', hint: 'Проекты домов, планировки, цены', done: false },
    );
    return clone(kp);
  },
  async declineProposal(id: string, reason: string) {
    await wait(200);
    const kp = must(proposals.find((p) => p.id === id), 'Предложение');
    kp.status = 'declined';
    kp.declineReason = reason;
    kp.decidedAt = nowIso();
    return clone(kp);
  },

  async signRequests() {
    await wait();
    return clone(signRequests);
  },
  async sign(id: string, typedName: string) {
    await wait(300);
    const s = must(signRequests.find((x) => x.id === id), 'Документ');
    if (s.status !== 'to_sign') throw new Error('Документ уже подписан');
    if (!s.documentId) throw new Error('Документа нет — попросите студию прислать файл');
    if (typedName.trim().length < 3) throw new Error('Введите имя и фамилию, как в документе');
    s.status = 'signed';
    s.signedAt = nowIso();
    s.signedBy = typedName.trim();
    s.hash = fakeHash(s.documentName + s.signedAt);
    return clone(s);
  },

  async onboarding(projectId: string) {
    await wait();
    return clone(onboarding.filter((o) => o.projectId === projectId && o.scope !== 'access'));
  },
  async completeOnboarding(itemId: string) {
    await wait(150);
    const o = must(onboarding.find((x) => x.id === itemId), 'Пункт');
    o.done = true;
    o.doneAt = nowIso();
    return clone(o);
  },

  async changes(projectId?: string) {
    await wait();
    const mine = new Set(projects.filter((p) => p.clientId === CLIENT_ID).map((p) => p.id));
    return changes.filter((c) => mine.has(c.projectId) && (!projectId || c.projectId === projectId)).map(forClient);
  },
  async change(id: string) {
    await wait();
    return forClient(changeDetail(must(changes.find((x) => x.id === id), 'Запрос'))) as ChangeDetail;
  },
  async requestChange(projectId: string, title: string, description: string, extra?: NewInitiative) {
    await wait(200);
    const at = nowIso();
    const files = (extra?.documentIds ?? []).map((id) => documents.find((d) => d.id === id)).filter((d) => !!d);
    const c: ChangeRequest = {
      id: `cr${changes.length + 1}`, projectId, title, description, goal: extra?.goal ?? '', urgency: extra?.urgency, author: people.me, createdAt: at, status: 'new',
      attachments: files.length ? files.map((d) => ({ id: d.id, name: d.name, sizeKb: d.sizeKb })) : undefined,
      history: [{ at, kind: 'created', by: people.me }],
    };
    fileChange(c);
    changes.unshift(c);
    return forClient(c);
  },
  async answerChange(id: string, text: string) {
    await wait(200);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    if (c.status !== 'question') throw new Error('Студия ни о чём не спрашивала');
    const history = changeHistory(c);
    c.status = 'new';
    c.question = null;
    pushChange(c, history, { at: nowIso(), kind: 'answered', by: people.me, text });
    return forClient(c);
  },
  async decideChange(id: string, accept: boolean) {
    await wait(250);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    if (c.status !== 'estimated' || !c.estimate) throw new Error('Оценки ещё нет');
    const history = changeHistory(c);
    c.status = accept ? 'accepted' : 'declined';
    c.decidedAt = nowIso();
    pushChange(c, history, { at: c.decidedAt, kind: accept ? 'accepted' : 'declined', by: people.me, side: accept ? undefined : 'client' });
    if (accept) {
      const inv = invoiceOfChange(c);
      c.invoiceId = inv.id;
      pushChange(c, history, { at: nowIso(), kind: 'invoice_issued', by: null, invoiceId: inv.id, number: inv.number });
      taskOfChange(c, history);
    }
    return forClient(c);
  },

  async reports(projectId: string) {
    await wait();
    return clone(reports.filter((r) => r.projectId === projectId && r.status !== 'draft'));
  },
  async meetings(projectId?: string) {
    await wait();
    const mine = new Set(projects.filter((p) => p.clientId === CLIENT_ID).map((p) => p.id));
    return clone(meetings.filter((m) => mine.has(m.projectId) && (!projectId || m.projectId === projectId)).sort((a, b) => a.at.localeCompare(b.at)).map((m) => Object.fromEntries(Object.entries(m).filter(([k]) => k !== 'transcriptUrl')) as Meeting));
  },
  async meetingIcs(id: string, series = false) {
    await wait();
    return icsBlob(id, series);
  },

  async notifications() {
    await wait(80);
    return clone(notifications);
  },
  async readNotifications() {
    notifications.forEach((n) => (n.read = true));
  },
  async prefs() {
    return clone(prefs);
  },
  async savePrefs(p: NotificationPrefs) {
    await wait(150);
    // As on the server: the first «seen» moment is kept.
    prefs = { ...p, tourSeenAt: prefs.tourSeenAt ?? p.tourSeenAt };
    return clone(prefs);
  },

  async payer() {
    const d = payersOf(CLIENT_ID)[0];
    return clone({ ...d.legal, email: d.email || payer.email });
  },
  async savePayer(p: PayerDetails) {
    await wait(200);
    checkRuLegal(p);
    payer = { ...p };
    const { email, ...legal } = p;
    Object.assign(payersOf(CLIENT_ID)[0], { legal, email });
    mirrorDefault(CLIENT_ID);
    return clone(payer);
  },
  async payers() {
    await wait();
    return payerSummaries(CLIENT_ID);
  },
  async payerDetails(id: string) {
    await wait();
    const p = mustPayer(CLIENT_ID, id);
    return clone({ ...p.legal, email: p.email });
  },
  async addPayer({ makeDefault, email, ...legal }: PayerDetails & { makeDefault?: boolean }) {
    await wait(200);
    checkRuLegal(legal);
    const id = `pay-${mockPayers.length + 1}`;
    mockPayers.push({ id, clientId: CLIENT_ID, legal: { kpp: '', ogrn: '', address: '', ...legal }, email: email ?? '', isDefault: false });
    if (makeDefault) makeDefaultPayer(CLIENT_ID, id);
    return payerSummaries(CLIENT_ID);
  },
  async updatePayer(id: string, { email, ...legal }: PayerDetails) {
    await wait(200);
    checkRuLegal(legal);
    Object.assign(mustPayer(CLIENT_ID, id), { legal, email: email ?? '' });
    mirrorDefault(CLIENT_ID);
    return payerSummaries(CLIENT_ID);
  },
  async setDefaultPayer(id: string) {
    await wait(150);
    makeDefaultPayer(CLIENT_ID, id);
    return payerSummaries(CLIENT_ID);
  },
  async colleagues() {
    await wait();
    return clone(karandash().contacts);
  },
  async inviteColleague({ name, email, role }: { name: string; email: string; role: ClientContact['role'] }) {
    await wait(200);
    const c = karandash();
    if (c.contacts.some((x) => x.email === email)) return { detail: 'Этот человек уже в вашей команде.', colleagues: clone(c.contacts) };
    c.contacts.push({ person: clientPerson(`u-${Date.now()}`, name, role === 'accountant' ? 'Бухгалтерия' : 'Сотрудник'), email, role, invited: 'pending' });
    return { detail: 'Приглашение отправлено. Ссылка действует 20 минут.', colleagues: clone(c.contacts) };
  },
  async updateColleague(id: string, patch: ColleaguePatch) {
    await wait(150);
    const c = karandash();
    const x = must(c.contacts.find((k) => k.person.id === id), 'Человек');
    const owners = c.contacts.filter((k) => k.role === 'owner' && !k.disabled && k.person.id !== id).length;
    if (patch.disabled && !x.disabled && x.person.id === people.me.id) throw new Error('Себя отключить нельзя');
    if (x.role === 'owner' && owners === 0 && ((patch.role && patch.role !== 'owner') || (patch.disabled && !x.disabled))) throw new Error('В кабинете должен остаться хотя бы один владелец');
    if (patch.role) x.role = patch.role;
    if (patch.disabled !== undefined) x.disabled = patch.disabled;
    return { detail: 'Сохранено', colleagues: clone(c.contacts) };
  },
  async resendInvitation(id: string) {
    await wait(150);
    const c = karandash();
    const x = must(c.contacts.find((k) => k.person.id === id), 'Человек');
    if (x.invited === 'accepted') throw new Error('Этот человек уже входил в кабинет');
    return { detail: 'Приглашение отправлено. Ссылка действует 20 минут.', colleagues: clone(c.contacts) };
  },

  async uploadDocument({ projectId, taskId, name, sizeKb }: { projectId?: string; taskId?: string; name: string; sizeKb: number }) {
    await wait(400);
    const d: DocumentRef = { id: `d${documents.length + 100}`, name, kind: 'other', projectId, uploadedBy: people.me, uploadedAt: nowIso(), sizeKb };
    documents.unshift(d);
    if (taskId) {
      const t = tasks.find((x) => x.id === taskId);
      if (t) {
        t.attachments.push({ id: d.id, name, sizeKb });
        post(t.threadId, people.me, `Загрузил файл: ${name}`, nowIso(), [{ id: d.id, name, sizeKb }]);
        rebuildThreads();
      }
    }
    return clone(d);
  },
  async statementOfAccount() {
    await wait();
    const mine = new Set(projects.filter((p) => p.clientId === CLIENT_ID).map((p) => p.id));
    const rows: { date: string; what: string; debit: Money; credit: Money }[] = [];
    for (const i of invoices.filter((x) => mine.has(x.projectId ?? ''))) {
      rows.push({ date: i.issuedAt, what: `Счёт № ${i.number}`, debit: i.total, credit: rub(0) });
      if (i.payment) rows.push({ date: i.payment.at, what: `Оплата по счёту № ${i.number}`, debit: rub(0), credit: i.paid });
    }
    rows.sort((a, b) => a.date.localeCompare(b.date));
    const balance = rows.reduce((n, r) => n + r.debit.amountMinor - r.credit.amountMinor, 0);
    return clone({ rows, balance: { amountMinor: balance, currency: 'RUB' as const } });
  },

  async sendFeedback(projectId: string, score: number, comment: string) {
    await wait(200);
    feedbacks.unshift({ id: `fb${feedbacks.length + 1}`, projectId, score, comment, at: nowIso(), context: projectId === 'site' ? 'Этап «Бриф» завершён' : 'Месяц работы' });
  },
  async renew(projectId: string) {
    await wait(250);
    const p = must(projects.find((x) => x.id === projectId), 'Проект');
    if (p.kind !== 'retainer') throw new Error('Продлеваются только регулярные платежи');
    const base = p.terms.until ? new Date(p.terms.until) : new Date();
    base.setMonth(base.getMonth() + 3);
    p.terms.until = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`;
    return clone(p);
  },
};

const studioExtras = {
  async overview() {
    const base = await baseStudio.overview();
    const extra: StudioAttention[] = [];
    changes.filter((c) => c.status === 'new').forEach((c) => extra.push({ kind: 'change', change: clone(c), client: clientName(c.projectId) }));
    proposals
      .filter((p) => p.status === 'sent' && new Date(p.validUntil).getTime() < Date.now())
      .forEach((p) => extra.push({ kind: 'proposal_silent', proposal: clone(p), client: clients.find((c) => c.id === p.clientId)?.name ?? '', days: Math.floor((Date.now() - new Date(p.sentAt ?? p.validUntil).getTime()) / 86_400_000) }));
    changes
      .filter((c) => c.status === 'estimated' && Date.now() - new Date(c.createdAt).getTime() >= 3 * 86_400_000)
      .forEach((c) => extra.push({ kind: 'change_unanswered', change: clone(c), client: clientName(c.projectId), days: Math.floor((Date.now() - new Date(c.createdAt).getTime()) / 86_400_000) }));
    const open = new Map<string, OnboardingItem[]>();
    onboarding.filter((o) => !o.done && o.scope !== 'access').forEach((o) => open.set(o.projectId, [...(open.get(o.projectId) ?? []), o]));
    open.forEach((items, projectId) => {
      const p = must(projects.find((x) => x.id === projectId), 'Проект');
      extra.push({ kind: 'onboarding', project: { id: p.id, title: p.title }, count: items.length, item: clone(items[0]), client: clientName(projectId) });
    });
    checkoutProblems.filter((c) => !c.resolvedAt).forEach((c) => extra.push({ kind: 'payment_issue', checkout: clone(c), invoiceId: c.invoiceId, link: `/money/${c.invoiceId}`, client: karandash().name }));
    invoices.filter((i) => i.status === 'paid' && !i.actDocumentId && acts.find((a) => a.id === i.actId)?.status !== 'signed' && acts.find((a) => a.id === i.actId)?.status !== 'to_sign').forEach((i) => extra.push({ kind: 'act_missing', invoice: clone(i), client: clientName(i.projectId ?? '') }));
    const attention = sortAttention([...base.attention, ...extra]);
    return { ...base, attention, urgentCount: attention.filter(isUrgent).length };
  },
  async proposals(clientId?: string) {
    await wait();
    return clone(proposals.filter((p) => !clientId || p.clientId === clientId));
  },
  async createProposal(input: { clientId: string; title: string; intro: string; kind: Proposal['kind']; catalogIds: string[]; milestones: Proposal['milestones']; prepaymentPercent: number; validDays: number }) {
    await wait(300);
    if (!Number.isInteger(input.prepaymentPercent) || input.prepaymentPercent < 0 || input.prepaymentPercent > 100) throw new Error('Предоплата — целое число от 0 до 100');
    const lines = input.catalogIds.map((id) => {
      const c = must(catalog.find((x) => x.id === id), 'Услуга');
      return { title: c.title, note: c.note, quantity: 1, price: c.price, total: c.price };
    });
    const kp: Proposal = {
      id: `kp${proposals.length + 1}`,
      clientId: input.clientId,
      number: `КП-${String(proposals.length + 3).padStart(3, '0')}`,
      title: input.title,
      intro: input.intro,
      kind: input.kind,
      lines,
      total: { amountMinor: lines.reduce((n, l) => n + l.total.amountMinor, 0), currency: 'RUB' },
      milestones: input.milestones,
      prepaymentPercent: input.prepaymentPercent,
      validUntil: day(input.validDays),
      status: 'sent',
      sentAt: nowIso(),
    };
    proposals.unshift(kp);
    const c = clients.find((x) => x.id === input.clientId);
    if (c && c.stage === 'lead') c.stage = 'proposal';
    if (input.clientId === CLIENT_ID) notify(`Новое предложение: ${kp.title}`, `/proposals/${kp.id}`);
    return clone(kp);
  },
  async sendForSignature(input: { clientId: string; kind: SignRequest['kind']; title: string; file: File; projectId?: string }) {
    await wait(250);
    if (input.file.size === 0) throw new Error('Файл пустой');
    if (input.file.type !== 'application/pdf') throw new Error('На подпись принимаем только PDF');
    const s: SignRequest = { id: `sg${signRequests.length + 1}`, kind: input.kind, title: input.title, documentName: input.file.name, documentId: 'd1', projectId: input.projectId, createdAt: nowIso(), status: 'to_sign' };
    signRequests.unshift(s);
    if (input.clientId === CLIENT_ID) notify(`На подпись: ${s.title}`, '/documents?tab=sign');
    return clone(s);
  },
  async signRequests() {
    await wait();
    return clone(signRequests);
  },

  async onboarding(projectId: string) {
    await wait();
    return clone(onboarding.filter((o) => o.projectId === projectId && o.scope !== 'access'));
  },
  async addOnboarding(projectId: string, item: Pick<OnboardingItem, 'kind' | 'title' | 'hint'> & Partial<Pick<OnboardingItem, 'scope' | 'toId'>>) {
    await wait(150);
    if (item.scope === 'access' && item.kind !== 'access') throw new Error('Вне запуска можно запросить только доступ');
    onboarding.push({ id: `ob${onboarding.length + 1}`, projectId, ...item, done: false });
    return clone(onboarding.filter((o) => o.projectId === projectId && o.scope !== 'access'));
  },
  async projectAccess(projectId: string) {
    await wait();
    const requests = onboarding
      .filter((o) => o.projectId === projectId && o.kind === 'access')
      .map((o) => ({ ...o, scope: o.scope ?? ('launch' as const), status: o.done ? ('done' as const) : ('open' as const) }))
      .reverse();
    const mine = secrets.filter((s) => s.projectId === projectId).map((s) => ({ ...publicSecret(s), mine: s.to.side === 'studio' }));
    return clone({ requests, secrets: mine });
  },
  async toggleOnboarding(itemId: string) {
    await wait(120);
    const o = must(onboarding.find((x) => x.id === itemId), 'Пункт');
    o.done = !o.done;
    o.doneAt = o.done ? nowIso() : undefined;
    return clone(o);
  },

  async changes(projectId?: string) {
    await wait();
    return clone(changes.filter((c) => !projectId || c.projectId === projectId));
  },
  async change(id: string) {
    await wait();
    return changeDetail(must(changes.find((x) => x.id === id), 'Запрос'));
  },
  async reinvoiceChange(id: string) {
    await wait(250);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    const detail = changeDetail(c);
    if (c.status !== 'accepted' || !detail.invoice) throw new Error('Счёт можно выставить заново только по принятому запросу');
    if (!detail.invoice.dead) throw new Error('Текущий счёт действует: сначала отмените его');
    const history = changeHistory(c);
    const old = c.invoiceId!;
    const inv = invoiceOfChange(c);
    c.invoiceIds = [...(c.invoiceIds ?? []), old];
    c.invoiceId = inv.id;
    pushChange(c, history, { at: nowIso(), kind: 'invoice_reissued', by: people.manager, invoiceId: inv.id, number: inv.number, previousInvoiceId: old, previousNumber: detail.invoice.number ?? undefined });
    notify(`Счёт по запросу «${c.title}» выставлен заново: № ${inv.number}`, `/p/${c.projectId}/changes`);
    return changeDetail(c);
  },
  async retryChangeIssue(id: string) {
    await wait(200);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    if (!c.linearPending) throw new Error('Задача в Linear не требуется или уже создана');
    c.linearPending = false;
    return clone(c);
  },
  async declineChange(id: string, reason: string) {
    await wait(200);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    if (![...OPEN, 'estimated'].includes(c.status)) throw new Error('Запрос уже решён');
    if (!reason.trim()) throw new Error('Нужна причина отказа');
    const history = changeHistory(c);
    c.status = 'declined';
    c.declineReason = reason.trim();
    c.declinedBy = 'studio';
    c.decidedAt = nowIso();
    pushChange(c, history, { at: c.decidedAt, kind: 'declined', by: people.manager, reason: c.declineReason, side: 'studio' });
    notify(`Запрос «${c.title}» отклонён: ${c.declineReason}`, `/p/${c.projectId}/changes`);
    return clone(c);
  },
  async askChange(id: string, text: string) {
    await wait(200);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    if (!OPEN.includes(c.status)) throw new Error('Запрос уже решён');
    const history = changeHistory(c);
    c.status = 'question';
    c.question = text;
    pushChange(c, history, { at: nowIso(), kind: 'asked', by: people.manager, text });
    notify(`Вопрос студии по запросу «${c.title}»`, `/p/${c.projectId}/changes`);
    return clone(c);
  },
  async parkChange(id: string, note: string) {
    await wait(200);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    if (c.status !== 'new' && c.status !== 'question') throw new Error('Отложить можно только новый запрос');
    const history = changeHistory(c);
    c.status = 'parked';
    c.parkNote = note;
    pushChange(c, history, { at: nowIso(), kind: 'parked', by: people.manager, note });
    notify(`Запрос «${c.title}» отложен`, `/p/${c.projectId}/changes`);
    return clone(c);
  },
  async estimateChange(id: string, estimate: ChangeRequest['estimate'] | 'in_scope' | { inScope: true; kind?: InitiativeKind; note?: string }) {
    await wait(200);
    const c = must(changes.find((x) => x.id === id), 'Запрос');
    if (!OPEN.includes(c.status)) throw new Error('Запрос уже решён');
    const history = changeHistory(c);
    if (estimate === 'in_scope' || (estimate && 'inScope' in estimate)) {
      const take: { kind?: InitiativeKind; note?: string } = estimate === 'in_scope' ? {} : estimate;
      if (take.kind) c.kind = take.kind;
      c.status = 'in_scope';
      c.decidedAt = nowIso();
      pushChange(c, history, { at: c.decidedAt, kind: 'in_scope', by: people.manager, note: take.note || undefined });
      taskOfChange(c, history);
    } else {
      c.status = 'estimated';
      c.estimate = estimate;
      pushChange(c, history, { at: nowIso(), kind: 'estimated', by: people.manager, price: estimate?.price, days: estimate?.days, note: estimate?.note || undefined });
    }
    notify(`Ответ по запросу «${c.title}»: ${c.status === 'in_scope' ? 'входит в текущий объём' : 'оценка готова'}`, `/p/${c.projectId}/changes`);
    return clone(c);
  },

  async publishReport(input: Omit<Report, 'id' | 'publishedAt' | 'status' | 'draftedAt' | 'author'> & { authorId: string }) {
    await wait(250);
    const { authorId, ...rest } = input;
    const author = must(studioTeam.find((p) => p.id === authorId), 'Автор');
    // One report per project and period: the same period updates it, and the client is told once.
    const old = reports.find((x) => x.projectId === rest.projectId && x.period === rest.period);
    if (old?.status === 'draft') {
      // A draft from the report pipeline: publishing it is when the client is told (MK42-5899).
      Object.assign(old, rest, { author, publishedAt: nowIso() });
      delete old.status;
      delete old.draftedAt;
      notify(`Опубликован отчёт: ${old.period}`, `/p/${old.projectId}/reports`);
      return clone(old);
    }
    if (old) {
      Object.assign(old, rest, { author });
      return clone(old);
    }
    const r: Report = { ...rest, id: `rp${reports.length + 1}`, publishedAt: nowIso(), author };
    reports.unshift(r);
    notify(`Опубликован отчёт: ${r.period}`, `/p/${r.projectId}/reports`);
    return clone(r);
  },
  async reports(projectId: string) {
    await wait();
    return clone(reports.filter((r) => r.projectId === projectId));
  },
  async meetings(projectId?: string) {
    await wait();
    return clone(meetings.filter((m) => !projectId || m.projectId === projectId).sort((a, b) => a.at.localeCompare(b.at)));
  },
  async planMeeting(input: MeetingInput) {
    await wait(200);
    if (input.link && !/^https:\/\/\S+$/.test(input.link)) throw new Error('Ссылка должна начинаться с https://');
    const start = new Date(input.at);
    const until = input.recurrence ? new Date(`${input.recurrence.until}T23:59:59Z`) : start;
    if (until < start) throw new Error('Повтор заканчивается раньше первой встречи');
    const seriesId = input.recurrence ? `s${meetings.length + 1}` : undefined;
    const made: Meeting[] = [];
    for (let n = 0; n < 26; n++) {
      const d = new Date(start);
      if (n) {
        if (!input.recurrence) break;
        if (input.recurrence.freq === 'monthly') d.setUTCMonth(start.getUTCMonth() + n);
        else d.setUTCDate(start.getUTCDate() + n * (input.recurrence.freq === 'weekly' ? 7 : 14));
      }
      if (d > until) break;
      made.push({ id: `mt${meetings.length + made.length + 1}`, projectId: input.projectId, at: d.toISOString(), title: input.title, durationMin: input.durationMin, agenda: input.agenda, link: input.link ?? null, status: 'planned', ...(seriesId ? { seriesId, recurrence: input.recurrence } : {}) });
    }
    meetings.push(...made);
    notify(`Встреча: ${input.title}${made.length > 1 ? ` (серия из ${made.length})` : ''}`, `/p/${input.projectId}/meetings`);
    return clone(made[0]);
  },
  async editMeeting(id: string, patch: MeetingPatch, scope: MeetingScope = 'one') {
    await wait(200);
    const m = must(meetings.find((x) => x.id === id), 'Встреча');
    if (isCanceled(m)) throw new Error('Встреча отменена');
    const delta = patch.at ? new Date(patch.at).getTime() - new Date(m.at).getTime() : 0;
    for (const x of seriesTargets(m, scope)) {
      const { at, ...rest } = patch;
      Object.assign(x, rest);
      if (at) x.at = x === m ? at : new Date(new Date(x.at).getTime() + delta).toISOString();
    }
    notify(`Встреча «${m.title}» изменена`, `/p/${m.projectId}/meetings`);
    return clone(m);
  },
  async cancelMeeting(id: string, scope: MeetingScope = 'one') {
    await wait(200);
    const m = must(meetings.find((x) => x.id === id), 'Встреча');
    if (isCanceled(m)) throw new Error('Встреча уже отменена');
    for (const x of seriesTargets(m, scope)) x.status = 'canceled';
    notify(`Встреча «${m.title}» отменена`, `/p/${m.projectId}/meetings`);
    return clone(m);
  },
  async recordOutcome(id: string, { transcriptUrl, ...outcome }: NonNullable<Meeting['outcome']> & { transcriptUrl?: string }) {
    await wait(200);
    const m = must(meetings.find((x) => x.id === id), 'Встреча');
    m.outcome = outcome;
    if (transcriptUrl) m.transcriptUrl = transcriptUrl;
    notify(`Итоги встречи «${m.title}»`, `/p/${m.projectId}/meetings`);
    return clone(m);
  },
  async firefliesDraft(id: string) {
    await wait(400);
    const m = must(meetings.find((x) => x.id === id), 'Встреча');
    // Synthetic: the demo has no Fireflies.
    return { title: m.title, summary: 'Демо-черновик: обсудили повестку и договорились о следующих шагах.', decisions: ['Первое демо-решение', 'Второе демо-решение'], transcriptUrl: 'https://app.fireflies.ai/view/demo' };
  },
  async meetingIcs(id: string, series = false) {
    await wait();
    return icsBlob(id, series);
  },

  async checkoutProblems() {
    await wait();
    return clone(checkoutProblems.filter((c) => !c.resolvedAt));
  },
  async resolveCheckout(id: string, note: string) {
    await wait(150);
    const c = must(checkoutProblems.find((x) => x.id === id), 'Платёж');
    c.resolvedAt = nowIso();
    c.resolvedNote = note;
    return clone(c);
  },
  async undelivered() {
    await wait();
    return clone(undelivered);
  },
  async retryNotification(id: string) {
    await wait(150);
    must(undelivered.find((x) => x.id === id), 'Уведомление');
    undelivered = undelivered.filter((x) => x.id !== id);
  },
  async statement() {
    await wait();
    return clone(statement.map((l) => ({ ...l, suggestions: suggestFor(l) })));
  },
  async addStatementLine(line: { date: string; amount: Money; payer: string; purpose: string }) {
    await wait(120);
    const l: StatementLine = { ...line, id: `st${statement.length + 1}`, status: 'unmatched', allocations: [], allocated: rub(0), suggestions: [] };
    statement.unshift(l);
    return clone(l);
  },
  async importStatement(file: File) {
    await wait(200);
    const rows = (await file.text()).split('\n').slice(1).filter((r) => r.trim());
    let added = 0;
    for (const r of rows) {
      const [date, amount, payer = '', purpose = ''] = r.split(';');
      const minor = Math.round(Number(amount.replace(',', '.')) * 100);
      if (!minor || minor <= 0) continue;
      if (statement.some((l) => l.date === date && l.amount.amountMinor === minor && l.payer === payer && l.purpose === purpose)) continue;
      statement.unshift({ id: `st${statement.length + 1}`, date, amount: { amountMinor: minor, currency: 'RUB' }, payer, purpose, status: 'unmatched', allocations: [], allocated: rub(0), suggestions: [] });
      added += 1;
    }
    return { added, duplicates: rows.length - added, unreadable: 0 };
  },
  async matchPayment(lineId: string, picks: StatementPick[]) {
    await wait(250);
    const line = must(statement.find((x) => x.id === lineId), 'Строка выписки');
    let left = line.amount.amountMinor - line.allocated.amountMinor;
    let first: Invoice | undefined;
    for (const p of picks) {
      const inv = must(invoices.find((x) => x.id === p.invoiceId), 'Счёт');
      const amountMinor = Math.min(p.amountMinor ?? inv.total.amountMinor, inv.total.amountMinor, left);
      left -= amountMinor;
      line.allocations.push({ invoiceId: inv.id, number: inv.number, amountMinor });
      if (amountMinor >= inv.total.amountMinor) markPaid(inv, 'transfer', `выписка от ${line.date}`);
      first ??= inv;
    }
    line.allocated = { amountMinor: line.amount.amountMinor - left, currency: line.amount.currency };
    line.status = 'matched';
    line.overpaid = left > 0 ? { amountMinor: left, currency: line.amount.currency } : null;
    return clone(must(first, 'Счёт'));
  },
  async unmatchLine(lineId: string) {
    await wait(150);
    const line = must(statement.find((x) => x.id === lineId), 'Строка выписки');
    Object.assign(line, { status: 'unmatched', allocations: [], allocated: rub(0), overpaid: null });
    return clone(line);
  },
  async ignoreLine(lineId: string) {
    await wait(150);
    const line = must(statement.find((x) => x.id === lineId), 'Строка выписки');
    line.status = 'ignored';
    return clone(line);
  },
  async cancelInvoice(invoiceId: string) {
    await wait(200);
    const inv = must(invoices.find((x) => x.id === invoiceId), 'Счёт');
    if (inv.status === 'paid') throw new Error('Счёт уже оплачен полностью или частично: отменить его нельзя');
    invoices.splice(invoices.indexOf(inv), 1);
    cancelledInvoices.push({ ...inv, status: 'cancelled' });
  },
  async markPaid(invoiceId: string, input: MarkPaidInput) {
    await wait(250);
    const inv = must(invoices.find((x) => x.id === invoiceId), 'Счёт');
    if (inv.status === 'paid') throw new Error(`Счёт № ${inv.number} уже оплачен`);
    const balance = inv.total.amountMinor - inv.paid.amountMinor;
    const amount = input.amountMinor ?? balance;
    if (input.date && input.date > day(0)) throw new Error('Дата оплаты не может быть в будущем');
    if (amount <= 0 || amount > balance) throw new Error('Сумма: от 0 до остатка');
    const method = input.method === 'cash' || input.method === 'card' ? input.method : 'transfer';
    const reference = `Отмечено вручную${input.note ? `: ${input.note}` : ''}`;
    if (amount === balance) markPaid(inv, method, reference);
    else inv.paid = { ...inv.paid, amountMinor: inv.paid.amountMinor + amount };
    return clone(inv);
  },
  async invoicePdf() {
    await wait();
    return new Blob(['%PDF-1.4 demo'], { type: 'application/pdf' });
  },
  async issueAct(invoiceId: string) {
    await wait(150);
    const inv = must(invoices.find((x) => x.id === invoiceId), 'Счёт');
    if (inv.status !== 'paid') throw new Error('Счёт ещё не оплачен');
    if (!inv.actId) markPaid(inv, inv.payment?.method ?? 'transfer', inv.payment?.reference ?? '');
    return clone(must(acts.find((a) => a.id === inv.actId), 'Акт'));
  },

  async remind(invoiceId: string) {
    await wait(150);
    const inv = must(invoices.find((x) => x.id === invoiceId), 'Счёт');
    if (inv.status !== 'sent' && inv.status !== 'overdue') throw new Error('Напомнить можно только по отправленному или просроченному счёту');
    if (reminders.has(invoiceId)) throw new Error('По этому счёту уже напоминали за последние 24 часа');
    const remindedAt = nowIso();
    reminders.set(invoiceId, remindedAt);
    notify(`Напоминание: счёт № ${inv.number} ждёт оплаты`, `/money/${inv.id}`);
    return { remindedAt };
  },

  async feedback() {
    await wait();
    return clone(feedbacks);
  },
  async accessRegistry(clientId: string) {
    await wait();
    const c = must(clients.find((x) => x.id === clientId), 'Клиент');
    const ids = new Set(c.contacts.map((x) => x.person.id));
    return secrets.filter((s) => ids.has(s.to.id) || ids.has(s.from.id)).map(publicSecret).map(clone);
  },
  async revokeAccess(id: string) {
    return baseClient.revokeSecret(id);
  },
  async incomingSecrets() {
    await wait();
    return secrets.filter((s) => s.to.side === 'studio').map(publicSecret).map(clone);
  },
  async openSecret(id: string) {
    await seeded;
    await wait(220);
    const s = must(secrets.find((x) => x.id === id), 'Доступ');
    if (s.status !== 'waiting' || !s.sealed) throw new Error('Доступ уже открыт или больше недействителен');
    const value = await decryptSecret(s.sealed);
    s.sealed = undefined;
    s.status = 'opened';
    s.openedAt = nowIso();
    return value;
  },
  async offboard(clientId: string) {
    await wait(300);
    const c = must(clients.find((x) => x.id === clientId), 'Клиент');
    const ids = new Set(c.contacts.map((x) => x.person.id));
    secrets.filter((s) => (ids.has(s.to.id) || ids.has(s.from.id)) && s.status === 'waiting').forEach((s) => ((s.sealed = undefined), (s.status = 'revoked')));
    c.stage = 'former';
    return clone(c);
  },
};

// --- «Интеграции»: keys of the public API (MK42-6384) -------------------------------------

const API_PRESETS: Record<ApiPreset, ApiScope[]> = {
  accounting: ['read:money', 'write:money'],
  reports: ['read:projects', 'read:reports'],
  ideas: ['read:initiatives', 'read:projects', 'write:initiatives'],
  read: ['read:documents', 'read:initiatives', 'read:money', 'read:projects', 'read:reports', 'read:tasks'],
};
const YEAR = 365 * 86_400_000;
const inDays = (d: number) => new Date(Date.now() + d * 86_400_000).toISOString();
type StoredKey = ApiKey & { clientId: string };
const apiKeyStore: StoredKey[] = [
  {
    id: 'key_demo1c', clientId: CLIENT_ID, name: '1С бухгалтерия', prefix: 'm42_ru_live_7Kq2', scopes: ['read:money', 'write:money'], projectId: null,
    createdAt: hoursAgo(24 * 20), expiresAt: new Date(NOW + YEAR - 20 * 86_400_000).toISOString(), lastUsedAt: hoursAgo(3), revokedAt: null, replacesId: null, replacedById: null, live: true, createdBy: people.me.name,
  },
  {
    id: 'key_demoamo', clientId: CLIENT_ID, name: 'amoCRM', prefix: 'm42_ru_live_Pz8d', scopes: ['read:initiatives', 'read:projects', 'write:initiatives'], projectId: 'site',
    createdAt: hoursAgo(24 * 5), expiresAt: new Date(NOW + YEAR - 5 * 86_400_000).toISOString(), lastUsedAt: hoursAgo(20), revokedAt: null, replacesId: null, replacedById: null, live: true, createdBy: people.me.name,
  },
];
const apiCalls: (ApiCall & { clientId: string })[] = Array.from({ length: 26 }, (_, i) => {
  const amo = i % 5 === 0;
  return {
    id: 26 - i, clientId: CLIENT_ID, at: hoursAgo(3 + i * 4), keyId: amo ? 'key_demoamo' : 'key_demo1c', keyName: amo ? 'amoCRM' : '1С бухгалтерия',
    method: amo ? 'POST' : 'GET', route: amo ? '/api/v1/initiatives' : i % 3 ? '/api/v1/invoices' : '/api/v1/acts', status: i === 7 ? 429 : amo ? 201 : 200, ms: 40 + ((i * 37) % 160), write: amo,
  };
});
const publicKey = (stored: StoredKey): ApiKey => {
  const k: Partial<StoredKey> & ApiKey = { ...stored };
  delete k.clientId;
  const live = !k.revokedAt && Date.parse(k.expiresAt) > Date.now();
  return { ...k, live, replacedById: apiKeyStore.find((x) => x.replacesId === k.id)?.id ?? null };
};
const B62 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
function mintKey(clientId: string, input: { name: string; scopes: ApiScope[]; projectId: string | null }, by: string, replaces?: StoredKey): IssuedApiKey {
  const c = must(clients.find((x) => x.id === clientId), 'Клиент');
  const secret = `m42_ru_${c.isTest ? 'test' : 'live'}_${Array.from({ length: 43 }, () => B62[Math.floor(Math.random() * 62)]).join('')}`;
  const k: StoredKey = {
    id: `key_${Math.random().toString(16).slice(2, 18)}`, clientId, name: input.name, prefix: secret.slice(0, 16), scopes: [...input.scopes].sort(), projectId: input.projectId,
    createdAt: nowIso(), expiresAt: inDays(365), lastUsedAt: null, revokedAt: null, replacesId: replaces?.id ?? null, replacedById: null, live: true, createdBy: by,
  };
  apiKeyStore.unshift(k);
  return { ...publicKey(k), secret };
}
function issueKey(clientId: string, input: NewApiKey, by: string): IssuedApiKey {
  const name = input.name.trim();
  if (!name) throw new ApiError(422, 'Назовите ключ: «1С бухгалтерия», «amoCRM» (до 120 символов)');
  const scopes = input.preset ? API_PRESETS[input.preset] : (input.scopes ?? []);
  if (!scopes.length) throw new ApiError(422, 'Выберите права ключа');
  if (apiKeyStore.filter((k) => k.clientId === clientId && publicKey(k).live).length >= 10) throw new ApiError(409, 'У компании уже 10 действующих ключей: отзовите ненужный');
  return mintKey(clientId, { name, scopes, projectId: input.projectId ?? null }, by);
}
function rotateKey(clientId: string, id: string, by: string): IssuedApiKey {
  const old = must(apiKeyStore.find((k) => k.id === id && k.clientId === clientId), 'Ключ');
  if (!publicKey(old).live) throw new ApiError(409, 'Ключ уже не действует: выпустите новый');
  if (apiKeyStore.some((k) => k.replacesId === id)) throw new ApiError(409, 'Ключ уже перевыпущен');
  const fresh = mintKey(clientId, { name: old.name, scopes: old.scopes, projectId: old.projectId }, by, old);
  old.expiresAt = new Date(Math.min(Date.parse(old.expiresAt), Date.now() + 7 * 86_400_000)).toISOString();
  return fresh;
}
function revokeKey(clientId: string, id: string): ApiKey {
  const k = must(apiKeyStore.find((x) => x.id === id && x.clientId === clientId), 'Ключ');
  k.revokedAt ??= nowIso();
  return publicKey(k);
}
const keysOf = (clientId: string) => apiKeyStore.filter((k) => k.clientId === clientId).map(publicKey);
function keyOwner() {
  if (workspace.me.clientRole !== 'owner') throw new ApiError(403, 'Ключи API — у владельца кабинета компании');
  if (!karandash().apiEnabled) throw new ApiError(404, 'API для компании не включено');
}
const apiKeysClient = {
  async apiKeys() {
    await wait();
    keyOwner();
    return clone(keysOf(CLIENT_ID));
  },
  async createApiKey(input: NewApiKey) {
    await wait(200);
    keyOwner();
    return issueKey(CLIENT_ID, input, workspace.me.name);
  },
  async rotateApiKey(id: string) {
    await wait(200);
    keyOwner();
    return rotateKey(CLIENT_ID, id, workspace.me.name);
  },
  async revokeApiKey(id: string) {
    await wait(150);
    keyOwner();
    return revokeKey(CLIENT_ID, id);
  },
  async apiDocs() {
    await wait();
    keyOwner();
    return clone(mockOpenapi);
  },
} satisfies Partial<PortalApi>;

const CALLS_PAGE = 20;
function integrationsOf(clientId: string, offset = 0): ClientIntegrations {
  const c = must(clients.find((x) => x.id === clientId), 'Клиент');
  const calls = apiCalls.filter((r) => r.clientId === clientId);
  return clone({
    clientId,
    apiEnabled: !!c.apiEnabled,
    isTest: !!c.isTest,
    mayIssue: !!c.isTest,
    keys: keysOf(clientId),
    calls: { items: calls.slice(offset, offset + CALLS_PAGE).map((r): ApiCall => ({ id: r.id, at: r.at, keyId: r.keyId, keyName: r.keyName, method: r.method, route: r.route, status: r.status, ms: r.ms, write: r.write })), total: calls.length, offset, limit: CALLS_PAGE },
  });
}
function testOnly(clientId: string) {
  if (!clients.find((x) => x.id === clientId)?.isTest) throw new ApiError(403, 'Ключи настоящего клиента выпускает и отзывает только владелец его кабинета');
}
const apiKeysStudio = {
  async integrations(clientId: string, offset = 0) {
    await wait();
    return integrationsOf(clientId, offset);
  },
  async setClientApi(clientId: string, on: boolean) {
    await wait(150);
    must(clients.find((x) => x.id === clientId), 'Клиент').apiEnabled = on;
    return integrationsOf(clientId);
  },
  async createClientApiKey(clientId: string, input: NewApiKey) {
    await wait(200);
    testOnly(clientId);
    return issueKey(clientId, input, people.manager.name);
  },
  async rotateClientApiKey(clientId: string, keyId: string) {
    await wait(200);
    testOnly(clientId);
    return rotateKey(clientId, keyId, people.manager.name);
  },
  async revokeClientApiKey(clientId: string, keyId: string) {
    await wait(150);
    testOnly(clientId);
    return revokeKey(clientId, keyId);
  },
} satisfies Partial<StudioApi>;

// --- Webhooks (MK42-6388) ------------------------------------------------------------------------

type StoredEndpoint = WebhookEndpoint & { clientId: string };
type StoredDelivery = WebhookDelivery & { clientId: string };
const WH_OFFERED = ['invoice.issued', 'report.published', 'task.needs_client', 'test.ping'];
const endpointStore: StoredEndpoint[] = [
  {
    id: 'whe_demo1c', clientId: CLIENT_ID, name: '1С бухгалтерия', url: 'https://1c.karandash.example/hooks/mark42', events: ['invoice.issued'], enabled: true,
    disabledReason: null, disabledAt: null, failureCount: 0, failingSince: null, lastSuccessAt: hoursAgo(5), secretRotating: false, createdAt: hoursAgo(24 * 12),
  },
  {
    id: 'whe_demoamo', clientId: CLIENT_ID, name: 'Битрикс24', url: 'https://karandash.bitrix24.example/rest/hooks', events: ['task.needs_client', 'report.published'], enabled: false,
    disabledReason: 'failing', disabledAt: hoursAgo(10), failureCount: 9, failingSince: hoursAgo(82), lastSuccessAt: hoursAgo(24 * 4), secretRotating: false, createdAt: hoursAgo(24 * 9),
  },
];
const deliveryStore: StoredDelivery[] = Array.from({ length: 14 }, (_, i): StoredDelivery => {
  const amo = i % 3 === 2;
  const bad = amo || i === 4;
  return {
    id: `whd_demo${String(i).padStart(2, '0')}`, clientId: CLIENT_ID, endpointId: amo ? 'whe_demoamo' : 'whe_demo1c', eventId: `evt_demo${i}`,
    type: amo ? (i % 2 ? 'report.published' : 'task.needs_client') : 'invoice.issued', status: bad ? (amo ? 'dead' : 'failed') : 'delivered', attempt: amo ? 7 : 1,
    nextAttemptAt: null, responseCode: amo ? 503 : i === 4 ? 410 : 200, error: amo ? 'http_503' : i === 4 ? 'http_410' : null, responseExcerpt: null,
    durationMs: 80 + ((i * 53) % 400), createdAt: hoursAgo(5 + i * 7), deliveredAt: bad ? null : hoursAgo(5 + i * 7), replayOf: null,
  };
});
const whPublic = (e: StoredEndpoint): WebhookEndpoint => {
  const x: Partial<StoredEndpoint> & WebhookEndpoint = { ...e };
  delete x.clientId;
  return clone(x);
};
const dPublic = (d: StoredDelivery): WebhookDelivery => {
  const x: Partial<StoredDelivery> & WebhookDelivery = { ...d };
  delete x.clientId;
  return clone(x);
};
const endpointsOf = (clientId: string) => endpointStore.filter((e) => e.clientId === clientId).map(whPublic);
const ownEndpoint = (clientId: string, id: string) => must(endpointStore.find((e) => e.id === id && e.clientId === clientId), 'Вебхук');
const whSecret = () => `whsec_${Array.from({ length: 32 }, () => B62[Math.floor(Math.random() * 62)]).join('')}`;
function deliveriesPage(clientId: string, f: WebhookDeliveryFilter = {}): WebhookDeliveriesPage {
  const limit = f.limit ?? 50;
  const all = deliveryStore
    .filter((d) => d.clientId === clientId && (!f.endpointId || d.endpointId === f.endpointId) && (!f.status || d.status === f.status) && (!f.event || d.type === f.event) && (!f.before || d.createdAt < f.before))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const items = all.slice(0, limit).map(dPublic);
  return { items, nextBefore: all.length > limit ? items[items.length - 1].createdAt : null };
}
function checkWebhookUrl(url: string) {
  if (!/^https:\/\/[^\s/]+/i.test(url.trim())) throw new ApiError(422, 'Только https://');
  if (/^https:\/\/(localhost|127\.|10\.|192\.168\.)/i.test(url.trim())) throw new ApiError(422, 'Адрес во внутренней сети: так нельзя');
}
function checkWebhookEvents(events: string[]) {
  if (!events.length || events.some((t) => !WH_OFFERED.includes(t))) throw new ApiError(422, `События — из списка: ${WH_OFFERED.join(', ')}`);
}
const webhooksClient = {
  async webhooks() {
    await wait();
    keyOwner();
    return { endpoints: endpointsOf(CLIENT_ID), events: WH_OFFERED.map((type) => ({ type, scope: null })), configured: true };
  },
  async createWebhook(input: NewWebhookEndpoint) {
    await wait(200);
    keyOwner();
    checkWebhookUrl(input.url);
    checkWebhookEvents(input.events);
    if (endpointStore.filter((e) => e.clientId === CLIENT_ID).length >= 5) throw new ApiError(409, 'У компании уже 5 вебхуков: удалите ненужный');
    const e: StoredEndpoint = {
      id: `whe_${Math.random().toString(16).slice(2, 18)}`, clientId: CLIENT_ID, name: input.description?.trim() ?? '', url: input.url.trim(), events: [...new Set(input.events)].sort(),
      enabled: false, disabledReason: 'unverified', disabledAt: null, failureCount: 0, failingSince: null, lastSuccessAt: null, secretRotating: false, createdAt: nowIso(),
    };
    endpointStore.push(e);
    return { ...whPublic(e), secret: whSecret() };
  },
  async updateWebhook(id: string, patch: WebhookPatch) {
    await wait(150);
    keyOwner();
    const e = ownEndpoint(CLIENT_ID, id);
    if (patch.events) {
      checkWebhookEvents(patch.events);
      e.events = [...new Set(patch.events)].sort();
    }
    if (patch.description !== undefined) e.name = patch.description.trim();
    if (patch.url !== undefined && patch.url.trim() !== e.url) {
      checkWebhookUrl(patch.url);
      Object.assign(e, { url: patch.url.trim(), enabled: false, disabledReason: 'unverified', disabledAt: nowIso() });
    }
    if (patch.enabled === true && !e.enabled) {
      if (['unverified', 'ping_failed', 'by_studio'].includes(e.disabledReason ?? '')) throw new ApiError(409, 'Сначала проверьте вебхук: сервер должен ответить 2xx на test.ping');
      Object.assign(e, { enabled: true, disabledReason: null, disabledAt: null, failureCount: 0, failingSince: null });
    } else if (patch.enabled === false && e.enabled) Object.assign(e, { enabled: false, disabledReason: 'by_user', disabledAt: nowIso() });
    return whPublic(e);
  },
  async deleteWebhook(id: string) {
    await wait(150);
    keyOwner();
    ownEndpoint(CLIENT_ID, id);
    endpointStore.splice(endpointStore.findIndex((e) => e.id === id), 1);
    for (let i = deliveryStore.length - 1; i >= 0; i--) if (deliveryStore[i].endpointId === id) deliveryStore.splice(i, 1);
    return { ok: true };
  },
  async rotateWebhookSecret(id: string) {
    await wait(150);
    keyOwner();
    const e = ownEndpoint(CLIENT_ID, id);
    e.secretRotating = true;
    return { ...whPublic(e), secret: whSecret() };
  },
  async pingWebhook(id: string) {
    await wait(300);
    keyOwner();
    const e = ownEndpoint(CLIENT_ID, id);
    // The demo's servers answer 2xx unless the address says otherwise.
    const ok = !/fail|down/i.test(e.url);
    const d: StoredDelivery = {
      id: `whd_${Math.random().toString(16).slice(2, 18)}`, clientId: CLIENT_ID, endpointId: id, eventId: `evt_${Math.random().toString(16).slice(2, 14)}`, type: 'test.ping',
      status: ok ? 'delivered' : 'failed', attempt: 1, nextAttemptAt: null, responseCode: ok ? 200 : 503, error: ok ? null : 'http_503', responseExcerpt: null, durationMs: 140,
      createdAt: nowIso(), deliveredAt: ok ? nowIso() : null, replayOf: null,
    };
    deliveryStore.push(d);
    if (ok && ['unverified', 'ping_failed', 'by_studio'].includes(e.disabledReason ?? '')) Object.assign(e, { enabled: true, disabledReason: null, disabledAt: null });
    else if (!ok && e.disabledReason === 'unverified') e.disabledReason = 'ping_failed';
    if (ok) e.lastSuccessAt = nowIso();
    return { delivery: dPublic(d), endpoint: whPublic(e) };
  },
  async webhookDeliveries(filter?: WebhookDeliveryFilter) {
    await wait();
    keyOwner();
    return deliveriesPage(CLIENT_ID, filter);
  },
  async replayWebhookDelivery(deliveryId: string) {
    await wait(150);
    keyOwner();
    const old = must(deliveryStore.find((d) => d.id === deliveryId && d.clientId === CLIENT_ID), 'Доставка');
    if (!ownEndpoint(CLIENT_ID, old.endpointId).enabled) throw new ApiError(409, 'Вебхук выключен: сначала включите его');
    const d: StoredDelivery = { ...old, id: `whd_${Math.random().toString(16).slice(2, 18)}`, status: 'pending', attempt: 0, responseCode: null, error: null, durationMs: null, createdAt: nowIso(), deliveredAt: null, nextAttemptAt: nowIso(), replayOf: old.id };
    deliveryStore.push(d);
    return dPublic(d);
  },
  async replayFailedWebhooks(id: string, since: string, until?: string) {
    await wait(200);
    keyOwner();
    const e = ownEndpoint(CLIENT_ID, id);
    if (!e.enabled) throw new ApiError(409, 'Вебхук выключен: сначала включите его');
    const latest = new Map<string, StoredDelivery>();
    deliveryStore
      .filter((d) => d.endpointId === id && d.type !== 'test.ping' && d.createdAt >= since && (!until || d.createdAt < until))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      .forEach((d) => latest.set(d.eventId, d));
    const todo = [...latest.values()].filter((d) => d.status === 'failed' || d.status === 'dead');
    for (const old of todo) deliveryStore.push({ ...old, id: `whd_${Math.random().toString(16).slice(2, 18)}`, status: 'pending', attempt: 0, responseCode: null, error: null, durationMs: null, createdAt: nowIso(), deliveredAt: null, replayOf: old.id });
    return { queued: todo.length };
  },
} satisfies Partial<PortalApi>;
const webhooksStudio = {
  async clientWebhooks(clientId: string): Promise<ClientWebhooks> {
    await wait();
    const c = must(clients.find((x) => x.id === clientId), 'Клиент');
    return { endpoints: endpointsOf(clientId), apiEnabled: !!c.apiEnabled };
  },
  async clientWebhookDeliveries(clientId: string, filter?: WebhookDeliveryFilter) {
    await wait();
    return deliveriesPage(clientId, filter);
  },
  async disableClientWebhook(clientId: string, endpointId: string) {
    await wait(150);
    const e = ownEndpoint(clientId, endpointId);
    if (e.enabled || e.disabledReason !== 'by_studio') Object.assign(e, { enabled: false, disabledReason: 'by_studio', disabledAt: nowIso() });
    return whPublic(e);
  },
} satisfies Partial<StudioApi>;
/** Endpoints the portal switched off after 3 days without a 2xx: the studio's «Внимание». */
const webhookAttention = (): StudioAttention[] =>
  endpointStore
    .filter((e) => e.disabledReason === 'failing')
    .map((e) => ({ kind: 'webhook_disabled', endpoint: { id: e.id, name: e.name, url: e.url, disabledAt: e.disabledAt }, client: must(clients.find((c) => c.id === e.clientId), 'Клиент').name, clientId: e.clientId }));

export const mockApi: PortalApi = { ...baseClient, ...clientExtras, ...apiKeysClient, ...webhooksClient };
/** Acts the studio assembles (demo). */
const studioActsMock = {
  async studioActs(filter) {
    await wait();
    return clone(
      acts
        .filter((a) => (!filter?.clientId || actClient(a) === filter.clientId) && (!filter?.projectId || a.projectId === filter.projectId))
        .map((a) => ({ ...a, clientId: actClient(a), clientName: clients.find((c) => c.id === actClient(a))?.name ?? null, actsEnabled: clients.find((c) => c.id === actClient(a))?.actsEnabled !== false, projectTitle: projects.find((p) => p.id === a.projectId)?.title ?? null })),
    );
  },
  async actSources({ clientId, projectId, periodFrom, periodTo, actId }) {
    await wait(150);
    const mine = projects.filter((p) => p.clientId === clientId && (!projectId || p.id === projectId));
    const used = new Set(acts.filter((a) => a.id !== actId && a.status !== 'void').flatMap((a) => (a.lines ?? []).map((l) => l.source ?? '')));
    const covered = new Set(acts.filter((a) => a.id !== actId && a.status !== 'void').flatMap((a) => a.invoiceIds ?? []));
    const line = (title: string, source: string, description = ''): ActSourceLine => ({ title, description, quantity: 1, unit: 'усл.', price: 0, source });
    return {
      milestones: mine.flatMap((p) =>
        p.kind === 'fixed' ? p.milestones.filter((m) => m.state === 'done').map((m) => ({ id: `milestone:${p.id}:${m.id}`, projectId: p.id, projectTitle: p.title, due: m.due ?? null, used: used.has(`milestone:${p.id}:${m.id}`), line: line(m.title, `milestone:${p.id}:${m.id}`, `Этап проекта «${p.title}»`) })) : [],
      ),
      tasks: tasks
        .filter((t) => mine.some((p) => p.id === t.projectId) && t.status === 'done' && (!periodFrom || t.updatedAt.slice(0, 10) >= periodFrom) && (!periodTo || t.updatedAt.slice(0, 10) <= periodTo))
        .map((t) => ({ id: `task:${t.key}`, key: t.key, url: t.linearUrl ?? '', projectId: t.projectId, projectTitle: projects.find((p) => p.id === t.projectId)?.title ?? '', doneAt: t.updatedAt.slice(0, 10), used: used.has(`task:${t.key}`), line: line(`${t.title} (${t.key})`, `task:${t.key}`) })),
      tasksUnavailable: false,
      invoices: invoices
        .filter((i) => i.status !== 'draft' && !covered.has(i.id) && (projects.find((p) => p.id === i.projectId)?.clientId ?? draftClient.get(i.id) ?? CLIENT_ID) === clientId && (!projectId || !i.projectId || i.projectId === projectId))
        .map((i) => ({ id: i.id, number: i.number, status: i.status, issuedAt: i.issuedAt, projectId: i.projectId ?? null, total: i.total, lines: i.lines.map((l) => ({ title: l.title, description: l.note ?? '', quantity: l.quantity, unit: l.unit ?? 'усл.', price: l.price.amountMinor, source: `invoice:${i.id}` })) })),
    };
  },
  async createAct(clientId, input) {
    await wait(250);
    const act: Act = { id: `a${acts.length + 1}`, number: input.number || nextActNumber(), issuedAt: input.date || day(0), invoiceId: input.invoiceIds[0] ?? null, status: 'draft', total: rub(0), fileName: '', clientId, lines: [], summary: '' };
    if (acts.some((a) => a.number === act.number)) throw new Error(`Акт № ${act.number} уже есть: возьмите другой номер`);
    fillAct(act, input);
    act.fileName = `Акт № ${act.number}.pdf`;
    acts.unshift(act);
    return clone(act);
  },
  async updateAct(id, patch) {
    await wait(200);
    const a = must(acts.find((x) => x.id === id), 'Акт');
    if (a.status !== 'draft') throw new Error('Акт уже отправлен: изменить можно только черновик');
    if (patch.number && acts.some((x) => x.id !== id && x.number === patch.number)) throw new Error(`Акт № ${patch.number} уже есть: возьмите другой номер`);
    fillAct(a, patch);
    a.fileName = `Акт № ${a.number}.pdf`;
    return clone(a);
  },
  async deleteAct(id) {
    await wait(150);
    const a = must(acts.find((x) => x.id === id), 'Акт');
    if (a.status !== 'draft') throw new Error('Акт уже отправлен: изменить можно только черновик');
    acts.splice(acts.indexOf(a), 1);
  },
  async sendAct(id) {
    await wait(300);
    const a = must(acts.find((x) => x.id === id), 'Акт');
    if (a.status !== 'draft') throw new Error('Акт уже отправлен: изменить можно только черновик');
    if (!a.lines?.length) throw new Error('В акте нет строк: добавьте хотя бы одну');
    if (a.total.amountMinor <= 0) throw new Error('Сумма акта должна быть больше нуля: укажите цены строк');
    Object.assign(a, { status: 'to_sign', documentId: 'd1', issuedAt: a.issuedAt });
    notify(`Акт № ${a.number} ждёт подписи`, '/money?tab=acts');
    return clone(a);
  },
  async voidAct(id) {
    await wait(150);
    const a = must(acts.find((x) => x.id === id), 'Акт');
    if (a.status !== 'to_sign') throw new Error(a.status === 'draft' ? 'Черновик удаляют, а не отзывают' : 'Подписанный акт отозвать нельзя');
    a.status = 'void';
    return clone(a);
  },
  async markActSigned(id, { via, signedOn, file }) {
    await wait(300);
    const a = must(acts.find((x) => x.id === id), 'Акт');
    if (a.status !== 'draft' && a.status !== 'to_sign') throw new Error('Акт уже подписан или отозван');
    if (a.status === 'draft' && (!a.lines?.length || a.total.amountMinor <= 0)) throw new Error('В акте нет строк или суммы');
    a.documentId ??= 'd1';
    Object.assign(a, { status: 'signed', signedVia: via, signedBy: null, signedAt: signedOn ?? day(0) });
    if (file) {
      const d: DocumentRef = { id: `d${documents.length + 100}`, name: `Скан акта № ${a.number}.${file.name.split('.').pop() ?? 'pdf'}`, kind: 'act_scan', projectId: a.projectId ?? undefined, uploadedBy: people.me, uploadedAt: nowIso(), sizeKb: Math.max(1, Math.round(file.size / 1024)) };
      documents.unshift(d);
      a.scanDocumentId = d.id;
    }
    return clone(a);
  },
  async studioActPdf() {
    await wait();
    return new Blob(['%PDF-1.4 demo act'], { type: 'application/pdf' });
  },
} satisfies Partial<StudioApi>;

export const mockStudioApi: StudioApi = { ...baseStudio, ...studioExtras, ...studioActsMock, ...apiKeysStudio, ...webhooksStudio };

// --- «Мой профиль» and people's cards ---------------------------------------------

const contacts: Record<string, { phone: string; messenger: string }> = {
  'u-pm': { phone: '+7 (921) 000-42-42', messenger: '@rodion' },
  'u-des': { phone: '', messenger: 'Telegram: @orlova' },
  'u-client': { phone: '+7 (999) 123-45-67', messenger: '' },
};
let sessions: MySession[] = [
  { createdAt: hoursAgo(2), lastSeenAt: hoursAgo(0.1), expiresAt: day(30), current: true },
  { createdAt: hoursAgo(80), lastSeenAt: hoursAgo(26), expiresAt: day(27), current: false },
];
const demoMe = (side: 'client' | 'studio') => (side === 'studio' ? people.manager : people.me);
const profileOf = (p: Person): MyProfile => ({
  ...p,
  email: access.get(p.id)?.email ?? clients.flatMap((c) => c.contacts).find((x) => x.person.id === p.id)?.email ?? `${p.id}@demo.example`,
  phone: contacts[p.id]?.phone ?? '',
  messenger: contacts[p.id]?.messenger ?? '',
});

let calendarSecret = 1;
const demoCalendar = (): CalendarLink => ({
  url: `https://portal.example/api/calendar/demo-token-${calendarSecret}.ics`,
  webcalUrl: `webcal://portal.example/api/calendar/demo-token-${calendarSecret}.ics`,
});

export const mockProfileApi: ProfileApi = {
  async calendar() {
    await wait(80);
    return demoCalendar();
  },
  async resetCalendar() {
    await wait();
    calendarSecret += 1;
    return demoCalendar();
  },
  async profile(side) {
    await wait(80);
    return profileOf(demoMe(side));
  },
  async saveProfile(side, patch: ProfilePatch) {
    await wait();
    const me = demoMe(side);
    if (patch.name !== undefined) {
      if (!patch.name.trim()) throw new ApiError(422, 'Имя не может быть пустым');
      me.name = patch.name.trim();
      me.initials = initialsOf(me.name);
    }
    if (patch.role !== undefined) me.role = patch.role.trim();
    if (patch.phone !== undefined && !/^[0-9+()\- ]*$/.test(patch.phone)) throw new ApiError(422, 'Телефон: только цифры, +, пробелы, скобки и дефис');
    contacts[me.id] = { ...(contacts[me.id] ?? { phone: '', messenger: '' }), ...(patch.phone !== undefined && { phone: patch.phone.trim() }), ...(patch.messenger !== undefined && { messenger: patch.messenger.trim() }) };
    log('profile_updated', me, { fields: Object.keys(patch) }, me);
    return profileOf(me);
  },
  async sessions() {
    await wait(80);
    return clone(sessions);
  },
  async revokeOthers() {
    await wait();
    const ended = sessions.filter((s) => !s.current).length;
    sessions = sessions.filter((s) => s.current);
    return { ended };
  },
  async prefs() {
    return clone(prefs);
  },
  async savePrefs(p) {
    await wait(150);
    // As on the server: the first «seen» moment is kept.
    prefs = { ...p, tourSeenAt: prefs.tourSeenAt ?? p.tourSeenAt };
    return clone(prefs);
  },
  async person(id) {
    await wait(80);
    const everyone = [...Object.values(people), ...clients.flatMap((c) => c.contacts.map((x) => x.person))];
    const p = everyone.find((x) => x.id === id);
    if (!p) throw new ApiError(404, 'Человек не найден');
    const client = clients.find((c) => c.contacts.some((x) => x.person.id === id));
    const mine = p.side === 'studio' ? projects.filter((x) => x.owner.id === id || x.team.some((t) => t.id === id)) : projects.filter((x) => x.clientId === client?.id);
    const a = access.get(id);
    const card: PersonCard = {
      ...profileOf(p),
      clientId: client?.id ?? (p.side === 'client' ? 'karandash' : null),
      clientName: client?.name ?? (p.side === 'client' ? 'Карандаш' : null),
      studioRole: p.side === 'studio' ? (a?.studioRole ?? 'specialist') : null,
      clientRole: p.side === 'client' ? (client?.contacts.find((x) => x.person.id === id)?.role ?? 'member') : null,
      disabled: false,
      projects: mine.map((x) => ({ id: x.id, title: x.title, clientId: x.clientId, lead: x.owner.id === id })),
    };
    return clone(card);
  },
};
